import React, { useEffect, useLayoutEffect, useState } from "react";
import { translateUi } from './i18n/index';
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  CheckCircle2,
  LayoutDashboard,
  Lock,
  Upload,
  KeyRound,
  Users,
  Shield,
  CircleX,
  XCircle,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ArrowLeftRight,
  Trash2,
  Printer,
  FileCheck,
  ClipboardList,
  Calendar,
  GraduationCap,
  TrainFront,
  Stethoscope,
  AlertTriangle,
  Award,
  BarChart3,
  Megaphone,
} from "./icons";
import { SETUP_WIZARD_DARK_STYLES } from "./onboardingDarkMode";
import { getOnboardingAddons, type AddonCatalogEntry } from "./addonCatalog";
import {
  TERMS_AND_CONDITIONS_FULL_TEXT,
  TERMS_SIGNATURE_VALUE_PLACEHOLDER,
  formatTermsDocumentForDisplay,
  applySignaturePreview,
  formatUkDateTime,
  buildTermsAgreementRecordHtml,
} from "./termsAgreementRecordHtml";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Shown next to disabled Continue / Submit buttons so users know which fields are still required. */
function RequiredFieldsHelper({
  missing,
  className,
}: {
  missing: string[];
  className?: string;
}) {
  if (missing.length === 0) return null;
  return (
    <div
      className={cn(
        "rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 max-w-prose",
        className
      )}
      role="status"
      aria-live="polite"
    >
      <p className="font-semibold">{translateUi("Complete the following to continue:")}</p>
      <ul className="mt-1 list-disc ml-4 space-y-0.5">
        {missing.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function RequiredMark() {
  return (
    <span className="text-rose-600 font-semibold" aria-hidden="true">
      {" "}
      *
    </span>
  );
}

const TERMS_SCROLL_THRESHOLD_PX = 8;

/** Omit date / timestamp / IP from the scrollable terms preview; those are shown in the UI elsewhere. */
function isOmittedTermsSignaturePreviewLine(line: string): boolean {
  const t = line.trim();
  return (
    /^Date\s*\((?:UTC|UK time)\)\s*:/i.test(t) ||
    /^Acceptance timestamp\s*\((?:UTC|UK time)\)\s*:/i.test(t) ||
    /^External IP address\s*:/i.test(t) ||
    /^IP address\s*:/i.test(t) ||
    /^Email address\s*:/i.test(t) ||
    /^Full name\s*:/i.test(t)
  );
}

function renderTermsScrollContent(
  fullName: string,
  jobTitle: string,
  signatureDataUrl: string | null,
  markdownText: string = TERMS_AND_CONDITIONS_FULL_TEXT
): React.ReactNode[] {
  const merged = applySignaturePreview(formatTermsDocumentForDisplay(markdownText || TERMS_AND_CONDITIONS_FULL_TEXT), {
    fullName,
    jobTitle,
  });
  const lines = merged.split("\n");
  const out: React.ReactNode[] = [];
  let key = 0;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (isOmittedTermsSignaturePreviewLine(line)) continue;
    if (!line.trim()) {
      out.push(<div key={`terms-sp-${key++}`} className="h-2" aria-hidden />);
      continue;
    }
    const sig = line.match(/^(Signed electronically by \(Customer\)|Job title):\s*(.*)$/);
    if (sig) {
      const sigRows: { label: string; value: string }[] = [];
      let j = i;
      while (j < lines.length) {
        const L = lines[j];
        if (isOmittedTermsSignaturePreviewLine(L)) {
          j += 1;
          continue;
        }
        if (!L.trim()) {
          j += 1;
          continue;
        }
        const m = L.match(/^(Signed electronically by \(Customer\)|Job title):\s*(.*)$/);
        if (!m) break;
        sigRows.push({ label: m[1], value: m[2] });
        j += 1;
      }
      i = j - 1;
      const customerRow = sigRows.find((r) => r.label === "Signed electronically by (Customer)");
      const jobRow = sigRows.find((r) => r.label === "Job title");
      const renderValueCell = (
        row: { label: string; value: string } | undefined,
        opts: { showSignatureUnder?: boolean },
        reactKey: string
      ) => {
        if (!row) return null;
        const isSignedElectronicallyRow = row.label === "Signed electronically by (Customer)";
        const rawValue = row.value.trim();
        const isUnderscorePlaceholder =
          rawValue === TERMS_SIGNATURE_VALUE_PLACEHOLDER || /^_+$/.test(rawValue);
        const displayValue = isUnderscorePlaceholder ? "" : row.value;
        const hasDisplay = displayValue.trim().length > 0;
        return (
          <div
            key={reactKey}
            className="grid grid-cols-1 gap-y-1 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] md:gap-x-5 md:items-start"
          >
            <span className="text-slate-600 text-xs sm:text-sm leading-snug">{row.label}:</span>
            <div className="min-w-0 space-y-2">
              <span
                className={cn(
                  "block min-w-0 leading-normal",
                  isSignedElectronicallyRow
                    ? cn(
                        "text-sm text-slate-900",
                        hasDisplay ? "font-normal break-words" : "font-bold text-slate-500"
                      )
                    : cn("font-serif font-bold text-slate-900 text-sm break-all")
                )}
              >
                {displayValue || "\u00a0"}
              </span>
              {opts.showSignatureUnder ? (
                <div className="h-24 w-full max-w-xs flex items-end">
                  {signatureDataUrl ? (
                    <img
                      src={signatureDataUrl}
                      alt=""
                      className="max-h-24 w-auto max-w-full rounded border border-slate-200 bg-white object-contain"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        );
      };
      out.push(
        <div key={`terms-sigblock-${key++}`} className="space-y-3 sm:space-y-4">
          {renderValueCell(customerRow, { showSignatureUnder: true }, "terms-sig-customer")}
          {renderValueCell(jobRow, { showSignatureUnder: false }, "terms-sig-job")}
        </div>
      );
      continue;
    }
    const isBulletLine = line.startsWith("• ");
    out.push(
      <p
        key={`terms-line-${key++}`}
        className={cn("rv-terms-line", isBulletLine && "pl-4")}
      >
        {isBulletLine ? (
          <>
            <span className="inline-block w-4 -ml-4 text-center" aria-hidden>
              •
            </span>
            {line.slice(2)}
          </>
        ) : (
          line
        )}
      </p>
    );
  }
  return out;
}

function isPublicIpv4(ip: string): boolean {
  if (!/^\d{1,3}(?:\.\d{1,3}){3}$/.test(ip)) return false;
  if (/^10\./.test(ip) || /^192\.168\./.test(ip) || /^172\.(1[6-9]|2\d|3[0-1])\./.test(ip)) return false;
  if (/^127\./.test(ip) || /^169\.254\./.test(ip)) return false;
  return true;
}

async function detectExternalIpv4ViaWebRtc(timeoutMs = 2200): Promise<string | null> {
  if (typeof window === "undefined" || typeof RTCPeerConnection === "undefined") return null;
  try {
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    const discovered = new Set<string>();
    pc.createDataChannel("local-ip-probe");

    const extractIpv4 = (candidateText: string): string | null => {
      const m = candidateText.match(/\b(\d{1,3}(?:\.\d{1,3}){3})\b/);
      return m?.[1] ?? null;
    };

    pc.onicecandidate = (event) => {
      const c = event.candidate?.candidate || "";
      const ip = extractIpv4(c);
      if (ip) discovered.add(ip);
      // Some browser implementations expose address directly.
      const addr = (event.candidate as unknown as { address?: string } | null)?.address;
      if (addr && /^\d{1,3}(?:\.\d{1,3}){3}$/.test(addr)) discovered.add(addr);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await new Promise<void>((resolve) => {
      const t = window.setTimeout(resolve, timeoutMs);
      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === "complete") {
          window.clearTimeout(t);
          resolve();
        }
      };
    });

    pc.close();
    const externalIp = Array.from(discovered).find((ip) => isPublicIpv4(ip));
    return externalIp || null;
  } catch {
    return null;
  }
}

/**
 * Helper: returns true once a user has scrolled a container to its bottom.
 */
function hasScrolledToBottom(element: HTMLElement | null, thresholdPx = TERMS_SCROLL_THRESHOLD_PX): boolean {
  if (!element) return false;
  const { scrollTop, scrollHeight, clientHeight } = element;
  return scrollTop + clientHeight >= scrollHeight - thresholdPx;
}

function formatDateUK(value: string | Date | null | undefined): string {
  if (!value) return "—";
  if (typeof value === "string") {
    const datePart = value.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split("-");
      return `${d}-${m}-${y}`;
    }
  }
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}
import { getApiAuthHeaders } from "./apiAuth";
import {
  CompanyDoc,
  activateCompanySetup,
  applyStandardCycle,
  getCompanyByCode,
  getCompanyFrameworks,
  getCompanyJobRoles,
  getCompanyRoleMappings,
  markCompanyContactPasswordChanged,
  saveCompanyJobRoles,
  saveCompanyRoleMappings,
  saveCompanySetupWizardStep,
} from "./companyOnboarding";
import { AVAILABLE_CYCLES, getCycleById, mergeFrameworkCycleOptions } from "./cycles";
import { CycleStructureView } from "./CycleStructureView";
import { CycleWizardPage } from "./cycle-builder/CycleWizardPage";
import { nominatedPersonFieldsComplete } from "../nominatedCompanyAdminValidation";

export interface CompanyDetailsDisplay {
  name: string;
  /** Full postal address */
  postalAddress: string;
  vatNumber: string;
  /** When registered office differs from postal address */
  registeredAddressIfDifferent: string;
  companyPhone: string;
  companyEmail: string;
  operatorType: "" | "railway_undertaking" | "infrastructure_manager";
  /** Legacy / admin — optional */
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  /** @deprecated use postalAddress */
  address: string;
}


interface Props {
  company: CompanyDoc;
  currentUserId: string;
  currentUserEmail?: string;
  onClose: () => void;
  onLogout?: () => void;
  /** Called after successful activation; carries optional server provisioning info when using the RTDB activate API. */
  onActivated?: (detail?: {
    companyName?: string;
    nominatedAdminEmail?: string;
    nominatedAdminName?: string;
    nominatedAdminJobTitle?: string;
    nominatedAdminProvisioning?: { provisioned?: boolean; emailed?: boolean; skipped?: boolean; warning?: string };
  }) => void;
  /** Called after a free activation or local 14-day trial succeeds so the parent can refresh companyAddons. */
  onAddonActivated?: () => void;
  /** When true, hides the header "Back to dashboard" action while onboarding is locked */
  hideBackToDashboard?: boolean;
  /** When true, company details are read-only (set by system admin); wizard starts at Job roles */
  lockCompanyDetails?: boolean;
  /** Controlled step - when provided, wizard step is managed by parent (persists across navigation) */
  activeStep?: StepId;
  onStepChange?: (step: StepId) => void;
  /** When true, user is on mobile/tablet - show horizontal step tabs instead of vertical sidebar */
  isMobileOrTablet?: boolean;
  isDarkMode?: boolean;
  /**
   * When true (default), onboarding uses Azure SQL API routes only — no SQL client writes.
   */
  azureOnly?: boolean;
  /**
   * System-admin onboarding step editor: save in place without advancing steps.
   * New-client onboarding keeps this false so Save & continue still progresses.
   */
  editorMode?: boolean;
  /**
   * System-admin preview of the live wizard without a real company.
   * No API writes; terms lock is off so every default step is reachable.
   */
  previewMode?: boolean;
}

type StepId =
  | "welcome"
  | "terms-and-conditions"
  | "company-details"
  | "change-password"
  | "job-roles"
  | "addons"
  | "framework"
  | "company-admin"
  | "role-mapping"
  | "review";

type OnboardingAddonRow = AddonCatalogEntry & {
  activated?: boolean;
  isFree?: boolean | null;
  stripeReady?: boolean | null;
  trialAvailable?: boolean | null;
  trialDays?: number | null;
};

/**
 * Always use the client catalog as the module list (so new modules ship with the UI),
 * and overlay activation / pricing flags from the API when present.
 * Prevents a stale server catalog from hiding modules on the welcome / activate screens.
 */
function mergeOnboardingAddonCatalog(remote: OnboardingAddonRow[] | null | undefined): OnboardingAddonRow[] {
  const byId = new Map<string, OnboardingAddonRow>();
  for (const row of Array.isArray(remote) ? remote : []) {
    const id = String(row?.id || "").trim();
    if (id) byId.set(id, row);
  }
  return getOnboardingAddons().map((base) => {
    const r = byId.get(base.id);
    if (!r) {
      return {
        ...base,
        activated: false,
        isFree: null,
        stripeReady: null,
        trialAvailable: true,
        trialDays: 14,
      };
    }
    return {
      ...base,
      activated: !!r.activated,
      isFree: r.isFree ?? null,
      stripeReady: r.stripeReady ?? null,
      trialAvailable: r.trialAvailable !== false,
      trialDays: typeof r.trialDays === "number" && r.trialDays > 0 ? r.trialDays : 14,
    };
  });
}

const baseSteps: { id: StepId; label: string }[] = [
  { id: "welcome", label: "Welcome" },
  { id: "terms-and-conditions", label: "Terms & Conditions" },
  { id: "company-details", label: "Company Details" },
  { id: "job-roles", label: "Job roles" },
  { id: "addons", label: "Add-ons" },
  { id: "company-admin", label: "Company administrator" },
  { id: "review", label: "Review & confirm" },
];

/** Job roles grouped by section for the wizard */
export const JOB_ROLE_SECTIONS: { title: string; roles: string[]; fullWidth?: boolean }[] = [
  {
    title: "Train Operations",
    roles: [
      "Train Driver",
      "Trainee Train Driver",
      "Driver Manager",
      "Instructor",
      "Senior Driver Instructor",
      "Driver Instructor / Driver Trainer",
      "Driver Assessor",
      "Train Manager / Guard",
      "Conductor",
      "Dispatch Staff / Platform Dispatcher",
      "Shunter / Shunter Driver",
      "Depot Driver",
      "Pilot",
    ],
  },
  {
    title: "Signalling & Control",
    roles: [
      "Signaller",
      "Signalling Technician / Maintainer",
      "Route Controller",
      "Electrical Control Operator (ECO)",
      "Rail Operating Centre (ROC) Controller",
      "Level Crossing Attendant / Keeper",
    ],
  },
  {
    title: "Engineering & Infrastructure",
    roles: [
      "Track Operative / Track Technician",
      "Controller of Site Safety (COSS)",
      "Engineering Supervisor (ES)",
      "Machine Controller",
      "Crane Controller",
      "Possession Support Staff (PSS)",
      "Engineering Train Driver",
    ],
  },
  {
    title: "Electrification & Plant",
    roles: [
      "Authorised Person (AP) – Electrical",
      "Overhead Line Equipment (OLE) Technician",
      "Linesman / OLE Maintainer",
      "Third Rail / DC Electrification Technician",
      "Nominated Person (NP)",
    ],
  },
  {
    title: "Train Preparation & Depot",
    roles: [
      "Fleet Technician",
      "Train Presentation Staff (if safety critical tasks involved)",
      "Depot Protection Staff",
      "Depot Shunter",
    ],
  },
  {
    title: "Safety & Operational Assurance",
    roles: [
      "Operations Standards Manager",
      "Internal Quality Assurance (IQA)",
      "Competence Assessor / Internal Quality Assurer",
      "Route Risk Assessor",
      "Safety Critical Trainer and IQA Verifier",
    ],
  },
  {
    title: "Administrative",
    roles: [
      "Human Resources",
      "Duty Control Operative",
      "Duty Control Manager",
    ],
    fullWidth: true,
  },
];

/**
 * Instructor / Senior Driver Instructor — locked when the company trains drivers
 * (passenger, freight, or Other + yes to training new train drivers).
 */
export const REQUIRED_ONBOARDING_JOB_ROLES = ["Instructor", "Senior Driver Instructor"] as const;

export type RailServiceType = "" | "passenger" | "freight" | "other";

/** Whether Instructor + Senior Driver Instructor must stay locked/selected. */
export function shouldLockInstructorJobRoles(
  serviceType: string | null | undefined,
  trainsNewDrivers: boolean | null | undefined,
): boolean {
  const t = String(serviceType ?? "")
    .trim()
    .toLowerCase();
  if (t === "passenger" || t === "freight") return true;
  if (t === "other") return trainsNewDrivers === true;
  // Legacy companies with no answer yet: keep previous locked behaviour.
  return true;
}

export function isRequiredOnboardingJobRole(name: string, lockInstructorRoles = true): boolean {
  if (!lockInstructorRoles) return false;
  const needle = String(name ?? "").trim().toLowerCase();
  if (!needle) return false;
  return REQUIRED_ONBOARDING_JOB_ROLES.some((r) => r.toLowerCase() === needle);
}

export function ensureRequiredOnboardingJobRoles<T extends { name: string; requiresCompetency: boolean }>(
  roles: T[],
  lockInstructorRoles = true,
): T[] {
  const out = Array.isArray(roles) ? [...roles] : [];
  if (!lockInstructorRoles) return out;
  for (const required of [...REQUIRED_ONBOARDING_JOB_ROLES].reverse()) {
    const idx = out.findIndex((r) => String(r.name ?? "").trim().toLowerCase() === required.toLowerCase());
    if (idx >= 0) {
      const [existing] = out.splice(idx, 1);
      out.unshift({ ...existing, name: required });
    } else {
      out.unshift({ name: required, requiresCompetency: false } as T);
    }
  }
  return out;
}

/** When instructor roles are unlocked, drop them from the default selection. */
export function deselectInstructorJobRolesIfUnlocked<T extends { name: string }>(
  roles: T[],
  lockInstructorRoles: boolean,
): T[] {
  if (lockInstructorRoles) return roles;
  const locked = new Set(REQUIRED_ONBOARDING_JOB_ROLES.map((r) => r.toLowerCase()));
  return roles.filter((r) => !locked.has(String(r.name ?? "").trim().toLowerCase()));
}

const ALL_PREDEFINED_ROLES = JOB_ROLE_SECTIONS.flatMap((s) => s.roles);

function isSignatureCanvasBlank(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx || canvas.width < 2 || canvas.height < 2) return true;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < data.length; i += 16) {
    const r = data[i] ?? 255;
    const g = data[i + 1] ?? 255;
    const b = data[i + 2] ?? 255;
    if (r < 248 || g < 248 || b < 248) return false;
  }
  return true;
}

/** Resize oversized logos so PATCH + Storage finish reliably (avoids hanging near ~88–92%). */
async function fileToLogoDataUrl(file: File, maxEdgePx = 1280): Promise<string> {
  const readRaw = () =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("Failed to read logo file"));
      reader.readAsDataURL(file);
    });

  // SVG / tiny files: skip canvas (SVG often fails to rasterise).
  if (/^image\/svg\+xml$/i.test(file.type) || file.size <= 180_000) {
    return readRaw();
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Could not read logo image"));
      el.src = objectUrl;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error("Could not read logo image dimensions");
    const scale = Math.min(1, maxEdgePx / Math.max(w, h));
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not prepare logo for upload");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, 0, 0, outW, outH);
    const preferJpeg = !/^image\/png$/i.test(file.type);
    if (preferJpeg) {
      const jpeg = canvas.toDataURL("image/jpeg", 0.88);
      if (jpeg && jpeg.startsWith("data:")) return jpeg;
    }
    return canvas.toDataURL("image/png");
  } catch {
    return readRaw();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** PATCH company logo via XHR so we can report upload progress (fetch has no upload progress). */
async function patchCompanyLogoWithProgress(options: {
  companyCode: string;
  dataUrl: string;
  headers: Record<string, string>;
  onUploadProgress: (pct0to100: number) => void;
  onProcessing?: () => void;
  timeoutMs?: number;
}): Promise<{ ok: boolean; status: number; data: { success?: boolean; error?: string; logoUrl?: string } }> {
  const { companyCode, dataUrl, onUploadProgress, onProcessing, timeoutMs = 45000 } = options;
  // XHR bypasses the global fetch auth interceptor — attach session Bearer token explicitly.
  const headers = await getApiAuthHeaders(options.headers);
  const body = JSON.stringify({ logoUrl: dataUrl });
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };
    xhr.open("PATCH", `/api/companies/${encodeURIComponent(companyCode)}/logo`);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.timeout = timeoutMs;
    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable || ev.total <= 0) return;
      onUploadProgress(Math.max(0, Math.min(100, Math.round((ev.loaded / ev.total) * 100))));
    };
    xhr.upload.onload = () => {
      // Bytes have left the browser; server may still be writing to Storage.
      onProcessing?.();
    };
    xhr.onload = () => {
      let data: { success?: boolean; error?: string; logoUrl?: string } = {};
      try {
        data = JSON.parse(xhr.responseText || "{}") as typeof data;
      } catch {
        /* ignore */
      }
      finish(() => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data }));
    };
    xhr.onerror = () => finish(() => reject(new Error("Network error while uploading logo")));
    xhr.ontimeout = () =>
      finish(() => reject(new Error("Logo upload timed out while saving. Try a smaller image, or try again.")));
    xhr.onabort = () => finish(() => reject(new Error("Logo upload was cancelled")));
    xhr.send(body);
  });
}

/** Draw signature with mouse, trackpad, or touch (pointer capture). */
function TermsSignaturePad({
  onSignatureChange,
}: {
  onSignatureChange: (dataUrl: string | null) => void;
}) {
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const drawingRef = React.useRef(false);
  const lastRef = React.useRef<{ x: number; y: number } | null>(null);
  const sizeRef = React.useRef({ w: 0, h: 0 });

  const syncFromCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSignatureChange(isSignatureCanvasBlank(canvas) ? null : canvas.toDataURL("image/png"));
  }, [onSignatureChange]);

  const fitCanvas = React.useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const rect = wrap.getBoundingClientRect();
    const cssW = Math.max(1, Math.floor(rect.width));
    const cssH = Math.max(1, Math.floor(rect.height));
    if (sizeRef.current.w === cssW && sizeRef.current.h === cssH && canvas.width > 1) return;

    // Preserve existing strokes across size changes (e.g. layout shift from terms preview).
    let prior: HTMLCanvasElement | null = null;
    if (canvas.width > 1 && canvas.height > 1 && !isSignatureCanvasBlank(canvas)) {
      prior = document.createElement("canvas");
      prior.width = canvas.width;
      prior.height = canvas.height;
      const pctx = prior.getContext("2d");
      pctx?.drawImage(canvas, 0, 0);
    }

    sizeRef.current = { w: cssW, h: cssH };
    const dpr = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cssW, cssH);
    if (prior) {
      ctx.drawImage(prior, 0, 0, cssW, cssH);
      // Keep parent preview in sync after a preserved resize; do not clear to null.
      onSignatureChange(isSignatureCanvasBlank(canvas) ? null : canvas.toDataURL("image/png"));
    } else {
      onSignatureChange(null);
    }
  }, [onSignatureChange]);

  useLayoutEffect(() => {
    fitCanvas();
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      // Never refit while the user is actively drawing — that jumps the pad under the pointer.
      if (drawingRef.current) return;
      fitCanvas();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [fitCanvas]);

  const canvasCoords = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0 && e.pointerType !== "touch" && e.pointerType !== "pen") return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    // Prevent browser scroll/zoom stealing the stroke (especially on touch).
    e.preventDefault();
    e.stopPropagation();
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const p = canvasCoords(e);
    lastRef.current = p;
    ctx.fillStyle = "#0f172a";
    ctx.fillRect(p.x - 1, p.y - 1, 2.5, 2.5);
    // Do not sync parent state here — that re-renders the terms preview and jumps the pad.
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    e.preventDefault();
    const p = canvasCoords(e);
    const last = lastRef.current;
    if (last) {
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f172a";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    lastRef.current = p;
    // Sync only when the stroke ends — avoids layout jumps mid-draw.
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    drawingRef.current = false;
    lastRef.current = null;
    syncFromCanvas();
  };

  const clear = () => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!wrap || !canvas || !ctx) return;
    const rect = wrap.getBoundingClientRect();
    const cssW = Math.max(1, Math.floor(rect.width));
    const cssH = Math.max(1, Math.floor(rect.height));
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cssW, cssH);
    onSignatureChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-600">{translateUi("Sign here")}</span>
        <button
          type="button"
          onClick={clear}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
        > {translateUi("Clear")} </button>
      </div>
      <div
        ref={wrapRef}
        className="relative w-full h-36 rounded-lg border border-slate-300 bg-white overflow-hidden touch-none overscroll-none"
      >
        <canvas
          ref={canvasRef}
          className="block w-full h-full cursor-crosshair touch-none"
          style={{ touchAction: "none" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={(e) => {
            if (drawingRef.current) endStroke(e);
          }}
        />
      </div>
      <p className="text-xs text-slate-500">{translateUi("Use your mouse, trackpad, finger, or stylus in this box.")}</p>
    </div>
  );
}

export const SetupWizard: React.FC<Props> = ({
  company,
  currentUserId,
  currentUserEmail,
  onClose,
  onLogout,
  onActivated,
  onAddonActivated,
  hideBackToDashboard = false,
  lockCompanyDetails = false,
  activeStep: controlledStep,
  onStepChange,
  isMobileOrTablet = false,
  isDarkMode = false,
  azureOnly = true,
  editorMode = false,
  previewMode = false,
}) => {
  const [internalStep, setInternalStep] = useState<StepId>(lockCompanyDetails ? "job-roles" : "welcome");
  const activeStep = controlledStep ?? internalStep;
  const setActiveStep = onStepChange ?? setInternalStep;
  const wizardRootRef = React.useRef<HTMLDivElement | null>(null);
  const stepContentScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [termsMarkdown, setTermsMarkdown] = useState(TERMS_AND_CONDITIONS_FULL_TEXT);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsScrolledToBottom, setTermsScrolledToBottom] = useState(false);
  const [termsAcceptanceHydrated, setTermsAcceptanceHydrated] = useState(lockCompanyDetails);
  const [termsAcceptanceSaving, setTermsAcceptanceSaving] = useState(false);
  const [termsAcceptanceError, setTermsAcceptanceError] = useState<string | null>(null);
  const [termsSignerName, setTermsSignerName] = useState("");
  const [termsSignerJobTitle, setTermsSignerJobTitle] = useState("");
  const [termsSignatureDataUrl, setTermsSignatureDataUrl] = useState<string | null>(null);
  const [termsAcceptedBy, setTermsAcceptedBy] = useState("");
  const [termsAcceptedAt, setTermsAcceptedAt] = useState<string | null>(null);
  const [termsAgreementStampedAt, setTermsAgreementStampedAt] = useState<string | null>(null);
  const [requesterExternalIpAddress, setRequesterExternalIpAddress] = useState("Detecting...");
  const termsScrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const termsSignatureSectionRef = React.useRef<HTMLDivElement | null>(null);
  const termsFormFocusScrollTimeoutRef = React.useRef<number | null>(null);

  /** Onboarding pages scroll inside App main (and sometimes nested panels) — reset all of them on step change. */
  useLayoutEffect(() => {
    const scrollElementToTop = (el: Element | null | undefined) => {
      if (!el || !(el instanceof HTMLElement)) return;
      el.scrollTop = 0;
      el.scrollLeft = 0;
    };

    const scrollScrollableAncestors = (from: HTMLElement | null) => {
      let node: HTMLElement | null = from;
      while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScrollY =
          (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
          node.scrollHeight > node.clientHeight + 1;
        if (canScrollY || node.scrollTop > 0) scrollElementToTop(node);
        node = node.parentElement;
      }
    };

    const resetOnboardingScroll = () => {
      scrollElementToTop(stepContentScrollRef.current);
      scrollElementToTop(document.querySelector(".rv-main-content"));
      scrollScrollableAncestors(wizardRootRef.current);
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    resetOnboardingScroll();
    // New step content can change height after paint — reset again on the next frames.
    const raf1 = window.requestAnimationFrame(() => {
      resetOnboardingScroll();
      window.requestAnimationFrame(resetOnboardingScroll);
    });
    const t = window.setTimeout(resetOnboardingScroll, 50);
    return () => {
      window.cancelAnimationFrame(raf1);
      window.clearTimeout(t);
    };
  }, [activeStep]);

  const [companyDetails, setCompanyDetails] = useState<CompanyDetailsDisplay>({
    name: company.name || company.companyCode || company.id || "",
    postalAddress: "",
    vatNumber: "",
    registeredAddressIfDifferent: "",
    companyPhone: "",
    companyEmail: "",
    operatorType: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    address: "",
  });
  const [companyDetailsLoaded, setCompanyDetailsLoaded] = useState(false);
  const [companyDetailsSaving, setCompanyDetailsSaving] = useState(false);
  const [companyDetailsError, setCompanyDetailsError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationIssues, setValidationIssues] = useState<{
    missingJobRoles: boolean;
    missingFramework: boolean;
    missingCompanyAdminNomination: boolean;
    missingRoleMappings: boolean;
    missingCompanyLogo: boolean;
  } | null>(null);
  const companyCode = company.companyCode || company.id;
  const [frameworkCycleId, setFrameworkCycleId] = useState<string>("");
  const [appliedFrameworks, setAppliedFrameworks] = useState<{ id: string; cycleId: string; name: string }[]>([]);
  const [applyingCycle, setApplyingCycle] = useState(false);
  const [frameworkError, setFrameworkError] = useState<string | null>(null);
  const [availableCycleOptions, setAvailableCycleOptions] = useState<{ id: string; name: string }[]>(
    AVAILABLE_CYCLES.map((c) => ({ id: c.id, name: c.name }))
  );
  const [availableCyclesLoading, setAvailableCyclesLoading] = useState(false);
  const [showCycleBuilder, setShowCycleBuilder] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadPhase, setLogoUploadPhase] = useState<"preparing" | "uploading" | "saving">("preparing");
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [contactNeedsPasswordChange, setContactNeedsPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [selectedJobRoles, setSelectedJobRoles] = useState<{ name: string; requiresCompetency: boolean }[]>([]);
  const [customRolesAdded, setCustomRolesAdded] = useState<{ name: string; requiresCompetency: boolean; addedAt: string; section?: string }[]>([]);
  const [jobRolesLoaded, setJobRolesLoaded] = useState(false);
  const [jobRolesSaving, setJobRolesSaving] = useState(false);
  const [jobRolesError, setJobRolesError] = useState<string | null>(null);
  const [railServiceType, setRailServiceType] = useState<RailServiceType>("");
  const [trainsNewDrivers, setTrainsNewDrivers] = useState<boolean | null>(null);
  const [railServiceSaving, setRailServiceSaving] = useState(false);
  const [railServiceError, setRailServiceError] = useState<string | null>(null);
  const lockInstructorRoles = shouldLockInstructorJobRoles(railServiceType, trainsNewDrivers);
  const railServiceAnswered =
    railServiceType === "passenger" ||
    railServiceType === "freight" ||
    (railServiceType === "other" && trainsNewDrivers !== null);
  const [otherRoleEnabledBySection, setOtherRoleEnabledBySection] = useState<Record<string, boolean>>({});
  const [otherRoleInputBySection, setOtherRoleInputBySection] = useState<Record<string, string>>({});
  const [nominatedAdminEmail, setNominatedAdminEmail] = useState("");
  const [nominatedAdminName, setNominatedAdminName] = useState("");
  const [nominatedAdminJobTitle, setNominatedAdminJobTitle] = useState("");
  /** use-contact = existing company contact; nominate-other = enter someone else; add-later = defer. */
  const [adminNominationChoice, setAdminNominationChoice] = useState<"use-contact" | "nominate-other" | "add-later">(
    "nominate-other"
  );
  const nominateAdminLater = adminNominationChoice === "add-later";
  const useExistingContactAsAdmin = adminNominationChoice === "use-contact";
  const [nominationSaving, setNominationSaving] = useState(false);
  const [nominationError, setNominationError] = useState<string | null>(null);
  const [hasNominationRecord, setHasNominationRecord] = useState(false);
  const [nominationDeferred, setNominationDeferred] = useState(false);
  const [onboardingAddons, setOnboardingAddons] = useState<OnboardingAddonRow[]>(() =>
    mergeOnboardingAddonCatalog(null)
  );
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [addonActionId, setAddonActionId] = useState<string | null>(null);
  const [addonsError, setAddonsError] = useState<string | null>(null);
  const [addonsMessage, setAddonsMessage] = useState<string | null>(null);
  const [roleMappingJobRoles, setRoleMappingJobRoles] = useState<{ name: string; requiresCompetency: boolean }[]>([]);
  const [roleMappings, setRoleMappings] = useState<{ jobRoleName: string; cycleId: string; cycleName: string; cycleLengthYears: number }[]>([]);
  const [roleMappingsLoaded, setRoleMappingsLoaded] = useState(false);
  const [roleMappingsSaving, setRoleMappingsSaving] = useState(false);
  const [roleMappingsError, setRoleMappingsError] = useState<string | null>(null);
  const [cycleStructureExpanded, setCycleStructureExpanded] = useState(false);
  const [cycleStructurePreviewId, setCycleStructurePreviewId] = useState<string>("");
  const [logoutSaving, setLogoutSaving] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [savedNoticeByStep, setSavedNoticeByStep] = useState<Partial<Record<StepId, string>>>({});
  const jobRolesDraftKey = React.useMemo(
    () => `rv_onboarding_job_roles_${companyCode}_${(currentUserEmail || "unknown").toLowerCase()}`,
    [companyCode, currentUserEmail]
  );
  const railServiceDraftKey = React.useMemo(
    () => `rv_onboarding_rail_service_${companyCode}_${(currentUserEmail || "unknown").toLowerCase()}`,
    [companyCode, currentUserEmail]
  );
  const loadJobRolesWithFallback = React.useCallback(async () => {
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    if (!azureOnly) {
      const jobRoles = await getCompanyJobRoles(companyCode).catch(() => []);
      if (Array.isArray(jobRoles) && jobRoles.length > 0) return jobRoles;
    }
    const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/job-roles`, { headers });
    if (!res.ok) return [];
    const data = await res.json().catch(() => []);
    return Array.isArray(data) ? data : [];
  }, [companyCode, currentUserEmail, azureOnly]);

  const saveActivationPrerequisites = React.useCallback(async (roles: { name: string; requiresCompetency: boolean }[]) => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    const companyCodesToTry = Array.from(
      new Set(
        [companyCode, company.companyCode, company.id]
          .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
          .map((v) => v.trim())
      )
    );

    const rolesToSave = ensureRequiredOnboardingJobRoles(roles, lockInstructorRoles);

    let rolesSaved = false;
    let rolesError: string | null = null;
    for (const codeToTry of companyCodesToTry) {
      const res = await fetch(`/api/companies/${encodeURIComponent(codeToTry)}/job-roles`, {
        method: "POST",
        headers,
        body: JSON.stringify({ roles: rolesToSave }),
      });
      if (res.ok) {
        rolesSaved = true;
        break;
      }
      const data = await res.json().catch(() => ({} as { error?: string }));
      rolesError = data.error || rolesError || "Failed to save job roles";
    }
    if (!rolesSaved) {
      if (azureOnly) throw new Error(rolesError || "Failed to save job roles");
      const fsRes = await saveCompanyJobRoles({ companyId: companyCode, roles: rolesToSave, userId: currentUserId });
      if (!fsRes.success) throw new Error(rolesError || fsRes.error || "Failed to save job roles");
    }

  }, [companyCode, company.companyCode, company.id, currentUserEmail, currentUserId, azureOnly, lockInstructorRoles]);

  const persistRailServiceAnswers = React.useCallback(async () => {
    if (previewMode || !companyCode || !railServiceAnswered) return;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    const body = {
      railServiceType,
      trainsNewDrivers: railServiceType === "other" ? trainsNewDrivers : null,
    };
    const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/rail-service`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({} as { error?: string }));
      throw new Error(data.error || "Failed to save service type");
    }
  }, [previewMode, companyCode, railServiceAnswered, currentUserEmail, railServiceType, trainsNewDrivers]);

  const loadJobRolesDraft = React.useCallback((): { selected: { name: string; requiresCompetency: boolean }[]; custom: { name: string; requiresCompetency: boolean; addedAt: string; section?: string }[] } | null => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(jobRolesDraftKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { selected?: { name: string; requiresCompetency: boolean }[]; custom?: { name: string; requiresCompetency: boolean; addedAt?: string; section?: string }[] };
      return {
        selected: Array.isArray(parsed.selected) ? parsed.selected : [],
        custom: Array.isArray(parsed.custom)
          ? parsed.custom.map((r) => ({ ...r, addedAt: r.addedAt || new Date().toISOString() }))
          : [],
      };
    } catch {
      return null;
    }
  }, [jobRolesDraftKey]);

  const saveJobRolesDraft = React.useCallback((selected: { name: string; requiresCompetency: boolean }[], custom: { name: string; requiresCompetency: boolean; addedAt: string; section?: string }[]) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(jobRolesDraftKey, JSON.stringify({ selected, custom }));
    } catch {
      // Ignore storage quota / private mode errors.
    }
  }, [jobRolesDraftKey]);

  const loadRailServiceDraft = React.useCallback((): {
    railServiceType: RailServiceType;
    trainsNewDrivers: boolean | null;
  } | null => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(railServiceDraftKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { railServiceType?: string; trainsNewDrivers?: boolean | null };
      const t = String(parsed.railServiceType ?? "").trim().toLowerCase();
      const serviceType: RailServiceType =
        t === "passenger" || t === "freight" || t === "other" ? t : "";
      const trains =
        parsed.trainsNewDrivers === true ? true : parsed.trainsNewDrivers === false ? false : null;
      return { railServiceType: serviceType, trainsNewDrivers: trains };
    } catch {
      return null;
    }
  }, [railServiceDraftKey]);

  const saveRailServiceDraft = React.useCallback(
    (serviceType: RailServiceType, trains: boolean | null) => {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(
          railServiceDraftKey,
          JSON.stringify({ railServiceType: serviceType, trainsNewDrivers: trains }),
        );
      } catch {
        /* ignore */
      }
    },
    [railServiceDraftKey],
  );

  useEffect(() => {
    const draft = loadRailServiceDraft();
    if (draft?.railServiceType) {
      setRailServiceType(draft.railServiceType);
      setTrainsNewDrivers(draft.railServiceType === "other" ? draft.trainsNewDrivers : null);
    }
  }, [loadRailServiceDraft]);

  useEffect(() => {
    if (!railServiceType) return;
    saveRailServiceDraft(railServiceType, railServiceType === "other" ? trainsNewDrivers : null);
  }, [railServiceType, trainsNewDrivers, saveRailServiceDraft]);

  useEffect(() => {
    setSelectedJobRoles((prev) => {
      const next = lockInstructorRoles
        ? ensureRequiredOnboardingJobRoles(prev, true)
        : deselectInstructorJobRolesIfUnlocked(prev, false);
      return next;
    });
  }, [lockInstructorRoles]);

  const markStepSaved = React.useCallback((step: StepId) => {
    const label = "Saved ✓";
    setSavedNoticeByStep((prev) => ({ ...prev, [step]: label }));
  }, []);

  const steps = React.useMemo(() => {
    const lockCompanySteps: { id: StepId; label: string }[] = [
      { id: "job-roles", label: translateUi("Job roles") },
      { id: "addons", label: "Add-ons" },
      { id: "company-admin", label: "Company administrator" },
      { id: "review", label: translateUi("Review & confirm") },
    ];
    if (contactNeedsPasswordChange) {
      return [
        { id: "change-password" as StepId, label: translateUi("Change password") },
        ...(lockCompanyDetails
          ? []
          : [
              { id: "welcome" as StepId, label: translateUi("Welcome") },
              { id: "terms-and-conditions" as StepId, label: translateUi("Terms & Conditions") },
              { id: "company-details" as StepId, label: "Company Details" },
            ]),
        ...(lockCompanyDetails
          ? lockCompanySteps
          : baseSteps.filter(
              (s) => s.id !== "welcome" && s.id !== "terms-and-conditions" && s.id !== "company-details"
            )),
      ];
    }
    return lockCompanyDetails ? lockCompanySteps : baseSteps;
  }, [contactNeedsPasswordChange, lockCompanyDetails]);

  useEffect(() => {
    if (previewMode) return;
    if (!termsAcceptanceHydrated) return;
    if (lockCompanyDetails || termsAccepted) return;
    if (contactNeedsPasswordChange) {
      if (activeStep !== "change-password") setActiveStep("change-password");
      return;
    }
    const allowedBeforeTerms = activeStep === "welcome" || activeStep === "terms-and-conditions";
    if (!allowedBeforeTerms) setActiveStep("welcome");
  }, [activeStep, contactNeedsPasswordChange, lockCompanyDetails, previewMode, setActiveStep, termsAcceptanceHydrated, termsAccepted]);

  const handleTermsScroll = React.useCallback(() => {
    if (termsScrolledToBottom) return;
    if (hasScrolledToBottom(termsScrollContainerRef.current)) {
      setTermsScrolledToBottom(true);
    }
  }, [termsScrolledToBottom]);

  /** Debounced: scroll terms preview + gently reveal the signature block when focusing Print name / Job title.
   * Vertical-only — never call scrollIntoView (iOS pans the page sideways and breaks the fixed layout). */
  const scheduleScrollTermsSignatureForFormFocus = React.useCallback(() => {
    if (termsFormFocusScrollTimeoutRef.current != null) {
      window.clearTimeout(termsFormFocusScrollTimeoutRef.current);
    }
    termsFormFocusScrollTimeoutRef.current = window.setTimeout(() => {
      termsFormFocusScrollTimeoutRef.current = null;
      const scrollEl = termsScrollContainerRef.current;
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
        scrollEl.scrollLeft = 0;
      }
      // Only nudge if the block is mostly off-screen — avoid yanking an already-visible pad.
      window.setTimeout(() => {
        const sigEl = termsSignatureSectionRef.current;
        if (!sigEl) return;
        const rect = sigEl.getBoundingClientRect();
        const vh = window.innerHeight || 0;
        const mostlyVisible = rect.top >= 0 && rect.bottom <= vh;
        if (!mostlyVisible) {
          const scrollParent =
            stepContentScrollRef.current ||
            (document.querySelector(".rv-main-content") as HTMLElement | null);
          if (scrollParent) {
            const parentRect = scrollParent.getBoundingClientRect();
            const delta = rect.top - parentRect.top - Math.max(12, parentRect.height * 0.08);
            scrollParent.scrollTop = Math.max(0, scrollParent.scrollTop + delta);
            scrollParent.scrollLeft = 0;
          }
        }
        if (typeof window !== "undefined" && window.scrollX) {
          window.scrollTo(0, window.scrollY);
        }
      }, 280);
    }, 120);
  }, []);

  useEffect(() => {
    return () => {
      if (termsFormFocusScrollTimeoutRef.current != null) {
        window.clearTimeout(termsFormFocusScrollTimeoutRef.current);
        termsFormFocusScrollTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (activeStep !== "terms-and-conditions") return;
    handleTermsScroll();
  }, [activeStep, handleTermsScroll]);

  const termsRecordDetailsReady =
    Boolean(termsSignerName.trim()) && Boolean(termsSignerJobTitle.trim()) && Boolean(termsSignatureDataUrl);

  const handleDownloadTermsAgreementRecord = React.useCallback(() => {
    if (!termsRecordDetailsReady || !termsSignatureDataUrl) return;
    const html = buildTermsAgreementRecordHtml({
      signerName: termsSignerName.trim(),
      signerJobTitle: termsSignerJobTitle.trim(),
      signerEmail: (currentUserEmail || "").trim(),
      signatureDataUrl: termsSignatureDataUrl,
      companyName: (company.name || companyDetails.name || companyCode || "").trim(),
      companyCode,
      externalIp: requesterExternalIpAddress,
      termsAgreementStampedAt,
      termsAccepted,
      termsAcceptedAt,
      markdown: termsMarkdown,
    });
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeCode = String(companyCode || "company").replace(/[^\w.-]+/g, "_");
    const safeStamp = formatUkDateTime(new Date()).replace(/[/\\:]/g, "-").replace(/\s+/g, "_");
    a.href = url;
    a.download = `rail-intel-terms-record-${safeCode}-${safeStamp}.html`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [
    termsRecordDetailsReady,
    termsSignatureDataUrl,
    termsSignerName,
    termsSignerJobTitle,
    currentUserEmail,
    company.name,
    companyDetails.name,
    companyCode,
    requesterExternalIpAddress,
    termsAgreementStampedAt,
    termsAccepted,
    termsAcceptedAt,
    termsMarkdown,
  ]);

  const handlePrintTermsAgreementRecord = React.useCallback(() => {
    if (!termsRecordDetailsReady || !termsSignatureDataUrl) return;
    const html = buildTermsAgreementRecordHtml({
      signerName: termsSignerName.trim(),
      signerJobTitle: termsSignerJobTitle.trim(),
      signerEmail: (currentUserEmail || "").trim(),
      signatureDataUrl: termsSignatureDataUrl,
      companyName: (company.name || companyDetails.name || companyCode || "").trim(),
      companyCode,
      externalIp: requesterExternalIpAddress,
      termsAgreementStampedAt,
      termsAccepted,
      termsAcceptedAt,
      markdown: termsMarkdown,
    });
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) {
      URL.revokeObjectURL(url);
      return;
    }
    const printDoc = () => {
      try {
        w.focus();
        w.print();
      } catch {
        /* ignore */
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    };
    /** Blob documents often load before a `load` listener is attached; defer print so layout + images resolve. */
    window.setTimeout(printDoc, 600);
  }, [
    termsRecordDetailsReady,
    termsSignatureDataUrl,
    termsSignerName,
    termsSignerJobTitle,
    currentUserEmail,
    company.name,
    companyDetails.name,
    companyCode,
    requesterExternalIpAddress,
    termsAgreementStampedAt,
    termsAccepted,
    termsAcceptedAt,
    termsMarkdown,
  ]);

  const termsAcceptanceInitialFetchDone = React.useRef(false);

  useEffect(() => {
    if (activeStep !== "terms-and-conditions") return;
    fetch("/api/request-context")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { externalIp?: string; ip?: string } | null) => {
        const externalIp =
          typeof data?.externalIp === "string" ? data.externalIp.trim() : typeof data?.ip === "string" ? data.ip.trim() : "";
        if (externalIp && isPublicIpv4(externalIp)) setRequesterExternalIpAddress(externalIp);
      })
      .catch(() => {});
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== "terms-and-conditions") return;
    detectExternalIpv4ViaWebRtc().then((externalIp) => {
      if (externalIp) setRequesterExternalIpAddress(externalIp);
    });
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== "terms-and-conditions") return;
    fetch("https://api.ipify.org?format=json")
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { ip?: string } | null) => {
        const externalIp = typeof data?.ip === "string" ? data.ip.trim() : "";
        if (externalIp && isPublicIpv4(externalIp)) setRequesterExternalIpAddress(externalIp);
      })
      .catch(() => {});
  }, [activeStep]);

  useEffect(() => {
    termsAcceptanceInitialFetchDone.current = false;
    setTermsAccepted(false);
    setTermsScrolledToBottom(false);
    setTermsAcceptanceError(null);
    setTermsSignerName("");
    setTermsSignerJobTitle("");
    setTermsSignatureDataUrl(null);
    setTermsAcceptedBy("");
    setTermsAcceptedAt(null);
    setTermsAgreementStampedAt(null);
    setRequesterExternalIpAddress("Detecting...");
    setTermsAcceptanceHydrated(lockCompanyDetails);
  }, [companyCode, currentUserEmail, lockCompanyDetails]);

  useEffect(() => {
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    fetch("/api/terms-document/current", { headers, cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { markdown?: string } | null) => {
        const md = typeof data?.markdown === "string" ? data.markdown.trim() : "";
        if (md) setTermsMarkdown(md);
      })
      .catch(() => {
        /* keep bundled fallback */
      });
  }, [currentUserEmail]);

  useEffect(() => {
    if (previewMode) {
      setTermsAcceptanceHydrated(true);
      return;
    }
    if (lockCompanyDetails || !companyCode) return;
    if (termsAcceptanceInitialFetchDone.current) return;
    termsAcceptanceInitialFetchDone.current = true;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    const url = currentUserEmail
      ? `/api/companies/${encodeURIComponent(companyCode)}?userEmail=${encodeURIComponent(currentUserEmail)}`
      : `/api/companies/${encodeURIComponent(companyCode)}`;
    fetch(url, { headers })
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (
          data: {
            termsAcceptedAt?: string | null;
            termsAcceptedBy?: string | null;
            railServiceType?: string | null;
            trainsNewDrivers?: boolean | number | null;
          } | null,
        ) => {
        if (data?.termsAcceptedAt) {
          setTermsAccepted(true);
          setTermsScrolledToBottom(true);
          setTermsAcceptedAt(data.termsAcceptedAt);
          setTermsAgreementStampedAt(data.termsAcceptedAt);
          if (data.termsAcceptedBy) setTermsAcceptedBy(data.termsAcceptedBy);
        }
        const t = String(data?.railServiceType ?? "").trim().toLowerCase();
        if (t === "passenger" || t === "freight" || t === "other") {
          setRailServiceType(t);
          if (t === "other") {
            const raw = data?.trainsNewDrivers;
            setTrainsNewDrivers(raw === true || raw === 1 ? true : raw === false || raw === 0 ? false : null);
          } else {
            setTrainsNewDrivers(null);
          }
        }
      })
      .catch(() => {})
      .finally(() => setTermsAcceptanceHydrated(true));
  }, [companyCode, currentUserEmail, lockCompanyDetails, previewMode]);

  // Framework setup is now done post-onboarding from the admin Cycle Builder.
  useEffect(() => {
    // System-admin editor may open framework/role-mapping; new-client onboarding skips them.
    if (!editorMode && (activeStep === "framework" || activeStep === "role-mapping")) {
      setActiveStep("company-admin");
    }
  }, [activeStep, setActiveStep, editorMode]);

  // Force password change first for temporary-password users (authoritative backend check).
  useEffect(() => {
    if (previewMode) return;
    if (!currentUserEmail || !companyCode) return;
    fetch(`/api/users/me/needs-password-change?companyCode=${encodeURIComponent(companyCode)}`, {
      headers: { "X-User-Email": currentUserEmail },
    })
      .then((res) => (res.ok ? res.json() : { needsChange: false }))
      .then((data: { needsChange?: boolean }) => {
        if (data?.needsChange) {
          setContactNeedsPasswordChange(true);
          setActiveStep("change-password");
        }
      })
      .catch(() => {});
  }, [companyCode, currentUserEmail, previewMode, setActiveStep]);

  const mapApiToCompanyDetails = React.useCallback(
    (data: Record<string, unknown> | CompanyDetailsDisplay): CompanyDetailsDisplay => {
      const postal = String((data as CompanyDetailsDisplay).postalAddress ?? (data as { address?: string }).address ?? "").trim();
      const op = (data as { operatorType?: string }).operatorType;
      // Initial invite went to contactEmail — company contact email is locked to that address.
      const inviteEmail = String((data as { contactEmail?: string }).contactEmail ?? "").trim();
      const fallbackSessionEmail = String(currentUserEmail ?? "").trim();
      const lockedCompanyEmail = inviteEmail || fallbackSessionEmail;
      return {
        name: String((data as { name?: string }).name ?? company.name ?? company.companyCode ?? company.id ?? ""),
        postalAddress: postal,
        vatNumber: String((data as { vatNumber?: string }).vatNumber ?? "").trim(),
        registeredAddressIfDifferent: String((data as { registeredAddressIfDifferent?: string }).registeredAddressIfDifferent ?? "").trim(),
        companyPhone: String((data as { companyPhone?: string }).companyPhone ?? (data as { contactPhone?: string }).contactPhone ?? "").trim(),
        companyEmail: lockedCompanyEmail || String((data as { companyEmail?: string }).companyEmail ?? "").trim(),
        operatorType: op === "railway_undertaking" || op === "infrastructure_manager" ? op : "",
        contactName: String((data as { contactName?: string }).contactName ?? "").trim(),
        contactEmail: inviteEmail || fallbackSessionEmail,
        contactPhone: String((data as { contactPhone?: string }).contactPhone ?? "").trim(),
        address: String((data as { address?: string }).address ?? "").trim(),
      };
    },
    [company.name, company.companyCode, company.id, currentUserEmail]
  );

  /** Email the initial company invite was sent to — company contact email is read-only. */
  const lockedInviteCompanyEmail = React.useMemo(() => {
    return (
      companyDetails.contactEmail.trim() ||
      String(currentUserEmail ?? "").trim() ||
      companyDetails.companyEmail.trim()
    );
  }, [companyDetails.contactEmail, companyDetails.companyEmail, currentUserEmail]);

  const existingContactForAdmin = React.useMemo(() => {
    const email = (
      companyDetails.contactEmail.trim() ||
      lockedInviteCompanyEmail ||
      String(currentUserEmail ?? "").trim()
    ).trim();
    const name = (
      companyDetails.contactName.trim() ||
      termsSignerName.trim()
    ).trim();
    return {
      email,
      name,
      /** Contact email is locked from the invite — that alone is enough to offer this option. */
      available: Boolean(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)),
    };
  }, [
    companyDetails.contactEmail,
    companyDetails.contactName,
    lockedInviteCompanyEmail,
    currentUserEmail,
    termsSignerName,
  ]);

  const applyExistingContactAsAdmin = React.useCallback(() => {
    setNominatedAdminEmail(existingContactForAdmin.email);
    setNominatedAdminName((prev) => {
      const fromContact = existingContactForAdmin.name.trim();
      if (fromContact.length >= 2) return fromContact;
      return prev.trim().length >= 2 ? prev : "";
    });
    setNominatedAdminJobTitle((prev) => (prev.trim().length >= 2 ? prev : translateUi("Company Administrator")));
  }, [existingContactForAdmin.email, existingContactForAdmin.name]);

  useEffect(() => {
    if (!lockedInviteCompanyEmail) return;
    setCompanyDetails((prev) => {
      if (prev.companyEmail === lockedInviteCompanyEmail && prev.contactEmail === lockedInviteCompanyEmail) {
        return prev;
      }
      return {
        ...prev,
        companyEmail: lockedInviteCompanyEmail,
        contactEmail: prev.contactEmail.trim() || lockedInviteCompanyEmail,
      };
    });
  }, [lockedInviteCompanyEmail]);

  /**
   * Prefill contact full name from the completed terms signer name when opening Company details.
   * Only sync while empty or while the stored value is still a prefix of the signer name
   * (avoids locking in the first two characters typed on the terms step).
   */
  useEffect(() => {
    if (activeStep !== "company-details") return;
    const signer = termsSignerName.trim();
    if (signer.length < 2) return;
    setCompanyDetails((prev) => {
      const current = prev.contactName.trim();
      if (!current || (current !== signer && signer.toLowerCase().startsWith(current.toLowerCase()))) {
        return { ...prev, contactName: signer };
      }
      return prev;
    });
  }, [activeStep, termsSignerName]);

  useEffect(() => {
    if (lockCompanyDetails && (activeStep === "company-details" || !companyDetailsLoaded)) {
      const url = currentUserEmail
        ? `/api/companies/${encodeURIComponent(companyCode)}?userEmail=${encodeURIComponent(currentUserEmail)}`
        : `/api/companies/${encodeURIComponent(companyCode)}`;
      fetch(url)
        .then((res) => res.ok ? res.json() : null)
        .then((data: (Record<string, unknown> & { contactNeedsPasswordChange?: boolean }) | null) => {
          if (data) {
            setCompanyDetails(mapApiToCompanyDetails(data));
            if (typeof data.logoUrl === "string" && data.logoUrl) setLogoUrl(data.logoUrl);
            setContactNeedsPasswordChange((prev) => prev || !!data.contactNeedsPasswordChange);
            if (data.contactNeedsPasswordChange) {
              setActiveStep("change-password");
            } else if (lockCompanyDetails && activeStep === "company-details") {
              setActiveStep("job-roles");
            }
          } else {
            setCompanyDetails((prev) => ({ ...prev, name: company.name || company.companyCode || company.id || prev.name }));
          }
          setCompanyDetailsLoaded(true);
        })
        .catch(() => setCompanyDetailsLoaded(true));
    }
  }, [lockCompanyDetails, companyCode, company.name, activeStep, companyDetailsLoaded, currentUserEmail, mapApiToCompanyDetails, setActiveStep]);

  const companyDetailsInitialFetchDone = React.useRef(false);
  useEffect(() => {
    companyDetailsInitialFetchDone.current = false;
  }, [companyCode]);

  useEffect(() => {
    if (previewMode || lockCompanyDetails || activeStep !== "company-details" || !companyCode) return;
    if (companyDetailsInitialFetchDone.current) return;
    companyDetailsInitialFetchDone.current = true;
    const url = currentUserEmail
      ? `/api/companies/${encodeURIComponent(companyCode)}?userEmail=${encodeURIComponent(currentUserEmail)}`
      : `/api/companies/${encodeURIComponent(companyCode)}`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: Record<string, unknown> | null) => {
        if (!data) return;
        setCompanyDetails(mapApiToCompanyDetails(data));
        if (typeof data.logoUrl === "string" && data.logoUrl) setLogoUrl(data.logoUrl);
      })
      .catch(() => {});
  }, [lockCompanyDetails, activeStep, companyCode, currentUserEmail, mapApiToCompanyDetails, previewMode]);


  // Reset job roles state when company changes
  useEffect(() => {
    setJobRolesLoaded(false);
  }, [companyCode]);


  // Load existing job roles when entering job-roles step (don't clear/delete saved data)
  useEffect(() => {
    if (activeStep === "job-roles" && !jobRolesLoaded && companyCode) {
      setJobRolesLoaded(true);
      const draft = loadJobRolesDraft();
      if (draft && (draft.selected.length > 0 || draft.custom.length > 0)) {
        setSelectedJobRoles(
          deselectInstructorJobRolesIfUnlocked(
            ensureRequiredOnboardingJobRoles(draft.selected, lockInstructorRoles),
            lockInstructorRoles,
          ),
        );
        setCustomRolesAdded(draft.custom);
      } else {
        setSelectedJobRoles(
          deselectInstructorJobRolesIfUnlocked(
            ensureRequiredOnboardingJobRoles([], lockInstructorRoles),
            lockInstructorRoles,
          ),
        );
      }
      if (!previewMode) {
        loadJobRolesWithFallback()
          .then((roles: { name: string; requiresCompetency: boolean }[]) => {
            if (Array.isArray(roles) && roles.length > 0) {
              const predefined = roles.filter((r) => ALL_PREDEFINED_ROLES.includes(r.name));
              const custom = roles.filter((r) => !ALL_PREDEFINED_ROLES.includes(r.name));
              setSelectedJobRoles(
                deselectInstructorJobRolesIfUnlocked(
                  ensureRequiredOnboardingJobRoles(
                    predefined.map((r) => ({ name: r.name, requiresCompetency: r.requiresCompetency })),
                    lockInstructorRoles,
                  ),
                  lockInstructorRoles,
                ),
              );
              setCustomRolesAdded(custom.map((r) => ({ name: r.name, requiresCompetency: r.requiresCompetency, addedAt: new Date().toISOString(), section: undefined })));
            } else {
              setSelectedJobRoles((prev) =>
                deselectInstructorJobRolesIfUnlocked(
                  ensureRequiredOnboardingJobRoles(prev, lockInstructorRoles),
                  lockInstructorRoles,
                ),
              );
            }
          })
          .catch(() => {
            setSelectedJobRoles((prev) =>
              deselectInstructorJobRolesIfUnlocked(
                ensureRequiredOnboardingJobRoles(prev, lockInstructorRoles),
                lockInstructorRoles,
              ),
            );
          });
      }
    } else if (activeStep === "job-roles" && !companyCode) {
      setJobRolesLoaded(true);
      setSelectedJobRoles(
        deselectInstructorJobRolesIfUnlocked(
          ensureRequiredOnboardingJobRoles([], lockInstructorRoles),
          lockInstructorRoles,
        ),
      );
    }
  }, [activeStep, companyCode, jobRolesLoaded, loadJobRolesWithFallback, loadJobRolesDraft, previewMode, lockInstructorRoles]);

  useEffect(() => {
    if (previewMode || activeStep !== "job-roles") return;
    saveJobRolesDraft(selectedJobRoles, customRolesAdded);
  }, [activeStep, selectedJobRoles, customRolesAdded, saveJobRolesDraft, previewMode]);

  const loadOnboardingAddons = React.useCallback(async () => {
    if (!companyCode && !previewMode) {
      setOnboardingAddons(mergeOnboardingAddonCatalog(null));
      return;
    }
    setAddonsLoading(true);
    setAddonsError(null);
    try {
      const headers: Record<string, string> = {};
      if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
      const catalogUrl = previewMode
        ? "/api/addons/catalog?onboardingOnly=1"
        : `/api/addons/catalog?onboardingOnly=1&companyCode=${encodeURIComponent(companyCode)}`;
      const res = await fetch(catalogUrl, { headers, cache: "no-store" });
      if (!res.ok) throw new Error("Could not load add-on catalogue");
      const data = (await res.json()) as { modules?: OnboardingAddonRow[] };
      setOnboardingAddons(mergeOnboardingAddonCatalog(data.modules));
    } catch (err: any) {
      setOnboardingAddons(mergeOnboardingAddonCatalog(null));
      setAddonsError(err?.message || "Could not refresh add-on status; showing catalogue.");
    } finally {
      setAddonsLoading(false);
    }
  }, [companyCode, currentUserEmail, previewMode]);

  useEffect(() => {
    if (activeStep === "addons" || activeStep === "welcome" || activeStep === "review") {
      void loadOnboardingAddons();
    }
  }, [activeStep, loadOnboardingAddons]);

  const handleActivateOrPurchaseAddon = React.useCallback(
    async (mod: OnboardingAddonRow, mode: "free" | "trial" | "purchase" = "free") => {
      if (previewMode) {
        setOnboardingAddons((prev) =>
          prev.map((m) => (m.id === mod.id ? { ...m, activated: true, trialAvailable: false } : m))
        );
        setAddonsMessage(translateUi("Preview only — nothing is saved."));
        return;
      }
      if (!companyCode || !currentUserEmail || mod.activated) return;
      setAddonActionId(mod.id);
      setAddonsError(null);
      setAddonsMessage(null);
      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "X-User-Email": currentUserEmail,
          "X-Company-Code": companyCode,
        };
        if (mode === "free" || (mode === "trial" && !mod.stripeReady)) {
          const endpoint = mode === "free" ? "/api/addons/activate-free" : "/api/addons/start-trial";
          const res = await fetch(endpoint, {
            method: "POST",
            headers,
            body: JSON.stringify({ companyCode, addonId: mod.id }),
          });
          const data = (await res.json().catch(() => ({}))) as {
            error?: string;
            trialExpiresAt?: string;
            trialDays?: number;
          };
          if (!res.ok) throw new Error(data.error || (mode === "free" ? translateUi("Activation failed") : translateUi("Could not start trial")));
          const days = data.trialDays || mod.trialDays || 14;
          setOnboardingAddons((prev) =>
            prev.map((m) => (m.id === mod.id ? { ...m, activated: true, trialAvailable: false } : m))
          );
          setAddonsMessage(
            mode === "free"
              ? `${mod.name} activated.`
              : `${mod.name} trial started (${days} days).`
          );
          onAddonActivated?.();
          await loadOnboardingAddons();
        } else {
          const res = await fetch("/api/addons/create-checkout-session", {
            method: "POST",
            headers,
            body: JSON.stringify({
              addonId: mod.id,
              companyCode,
              returnTab: "addons",
              ...(mode === "trial" ? { startTrial: true } : {}),
            }),
          });
          const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
          if (!res.ok || !data.url) throw new Error(data.error || "Could not start checkout");
          window.location.href = data.url;
        }
      } catch (err: any) {
        setAddonsError(err?.message || "Could not activate or purchase this add-on.");
      } finally {
        setAddonActionId(null);
      }
    },
    [companyCode, currentUserEmail, loadOnboardingAddons, onAddonActivated, previewMode]
  );

  const fetchValidation = React.useCallback(async () => {
    if (previewMode) {
      const hasLocalRoles = selectedJobRoles.length + customRolesAdded.length > 0;
      return {
        missingJobRoles: !hasLocalRoles,
        missingFramework: false,
        missingCompanyAdminNomination: !hasNominationRecord && !nominationDeferred,
        missingRoleMappings: false,
        missingCompanyLogo: !logoUrl,
      };
    }
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    const [roles, nominationRes] = await Promise.all([
      loadJobRolesWithFallback().catch(() => [] as { name: string; requiresCompetency: boolean }[]),
      fetch(`/api/companies/${encodeURIComponent(companyCode)}/nominate-company-admin`, { headers })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null) as Promise<{
        hasNomination?: boolean;
        deferred?: boolean;
        nominationStepComplete?: boolean;
      } | null>,
    ]);
    const hasLocalRoles = selectedJobRoles.length + customRolesAdded.length > 0;
    /** Server stores the nomination or an explicit "add later" deferral. */
    const nominationOk =
      nominationRes?.nominationStepComplete === true ||
      nominationRes?.hasNomination === true ||
      nominationRes?.deferred === true ||
      hasNominationRecord ||
      nominationDeferred;
    return {
      missingJobRoles: !hasLocalRoles && (!Array.isArray(roles) || roles.length === 0),
      missingFramework: false,
      missingCompanyAdminNomination: !nominationOk,
      missingRoleMappings: false,
      missingCompanyLogo: !logoUrl,
    };
  }, [
    companyCode,
    currentUserEmail,
    loadJobRolesWithFallback,
    selectedJobRoles,
    customRolesAdded,
    logoUrl,
    hasNominationRecord,
    nominationDeferred,
    previewMode,
  ]);

  const fetchAppliedFrameworks = React.useCallback(async () => {
    if (!companyCode) return;
    const data = await getCompanyFrameworks(companyCode);
    setAppliedFrameworks(Array.isArray(data) ? data : []);
  }, [companyCode]);

  const fetchAvailableCycles = React.useCallback(async () => {
    if (!companyCode) return;
    setAvailableCyclesLoading(true);
    try {
      const headers: Record<string, string> = {};
      if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
      const res = await fetch(
        `/api/cycles?companyCode=${encodeURIComponent(companyCode)}&assignableOnly=true&v=${Date.now()}`,
        { headers, cache: "no-store" }
      );
      if (res.ok) {
        const rows = await res.json().catch(() => []);
        setAvailableCycleOptions(mergeFrameworkCycleOptions(Array.isArray(rows) ? rows : [], companyCode));
      } else {
        setAvailableCycleOptions(AVAILABLE_CYCLES.map((c) => ({ id: c.id, name: c.name })));
      }
    } catch {
      setAvailableCycleOptions(AVAILABLE_CYCLES.map((c) => ({ id: c.id, name: c.name })));
    } finally {
      setAvailableCyclesLoading(false);
    }
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    if ((activeStep === "framework" || activeStep === "role-mapping") && companyCode) {
      fetchAppliedFrameworks();
    }
    if (activeStep === "framework" && companyCode) {
      fetchAvailableCycles();
    }
  }, [activeStep, companyCode, fetchAppliedFrameworks, fetchAvailableCycles]);

  useEffect(() => {
    setRoleMappingsLoaded(false);
  }, [companyCode]);

  useEffect(() => {
    if (activeStep === "role-mapping" && !roleMappingsLoaded && companyCode) {
      setRoleMappingsLoaded(true);
      if (azureOnly) {
        const headers: Record<string, string> = {};
        if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
        Promise.all([
          fetch(`/api/companies/${encodeURIComponent(companyCode)}/job-roles`, { headers }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
          fetch(`/api/companies/${encodeURIComponent(companyCode)}/role-mappings`, { headers }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
        ])
          .then(([roles, mappings]: [Array<{ name: string; requiresCompetency: boolean }>, Array<{ jobRoleName: string; cycleId: string; cycleName: string; cycleLengthYears: number }>]) => {
            setRoleMappingJobRoles(Array.isArray(roles) ? roles : []);
            setRoleMappings(Array.isArray(mappings) ? mappings : []);
          })
          .catch(() => {});
      } else {
        Promise.all([
          getCompanyJobRoles(companyCode),
          getCompanyRoleMappings(companyCode),
        ])
          .then(([roles, mappings]: [Array<{ name: string; requiresCompetency: boolean }>, Array<{ jobRoleName: string; cycleId: string; cycleName: string; cycleLengthYears: number }>]) => {
            setRoleMappingJobRoles(Array.isArray(roles) ? roles : []);
            const maps = Array.isArray(mappings) ? mappings : [];
            setRoleMappings(maps);
          })
          .catch(() => {});
      }
    } else if (activeStep === "role-mapping" && !companyCode) {
      setRoleMappingsLoaded(true);
    }
  }, [activeStep, companyCode, roleMappingsLoaded, azureOnly, currentUserEmail]);

  useEffect(() => {
    if (previewMode || activeStep !== "company-admin" || !companyCode) return;
    const headers: Record<string, string> = {};
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
    fetch(`/api/companies/${encodeURIComponent(companyCode)}/nominate-company-admin`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          data: {
            hasNomination?: boolean;
            deferred?: boolean;
            nominationStepComplete?: boolean;
            adminEmail?: string | null;
            adminFullName?: string | null;
            adminJobTitle?: string | null;
          } | null
        ) => {
          if (data?.deferred) {
            setNominationDeferred(true);
            setAdminNominationChoice("add-later");
            setHasNominationRecord(false);
          } else if (data?.hasNomination) {
            setHasNominationRecord(true);
            setNominationDeferred(false);
            const savedEmail = String(data.adminEmail ?? "").trim().toLowerCase();
            const contactEmail = existingContactForAdmin.email.trim().toLowerCase();
            if (data.adminEmail) setNominatedAdminEmail(String(data.adminEmail));
            if (data.adminFullName) setNominatedAdminName(String(data.adminFullName));
            if (data.adminJobTitle) setNominatedAdminJobTitle(String(data.adminJobTitle));
            setAdminNominationChoice(
              savedEmail && contactEmail && savedEmail === contactEmail ? translateUi("use-contact") : translateUi("nominate-other")
            );
          }
        }
      )
      .catch(() => {});
  }, [activeStep, companyCode, currentUserEmail, existingContactForAdmin.email, previewMode]);

  useEffect(() => {
    if (activeStep === "review") {
      (async () => {
        try {
          setValidating(true);
          const issues = await fetchValidation();
          setValidationIssues(issues);
        } finally {
          setValidating(false);
        }
      })();
    }
  }, [activeStep, companyCode, fetchValidation]);

  useEffect(() => {
    if (previewMode) return;
    getCompanyByCode(companyCode)
      .then((data: { logoUrl?: string } | null) => {
        if (data?.logoUrl) setLogoUrl(data.logoUrl);
      })
      .catch(() => {});
  }, [companyCode, previewMode]);

  const handleActivate = async () => {
    if (previewMode) return;
    setLogoUploadError(null);
    try {
      setValidating(true);
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
      if (!termsAcceptanceHydrated || !termsAccepted) {
        setValidationIssues((prev) => ({
          ...(prev || {
            missingJobRoles: false,
            missingFramework: false,
            missingCompanyAdminNomination: false,
            missingRoleMappings: false,
            missingCompanyLogo: !logoUrl,
          }),
          activateError: "Terms & Conditions must be reviewed and accepted before activation.",
        }));
        return;
      }
      const rolesToPersist = [
        ...selectedJobRoles,
        ...customRolesAdded.map((r) => ({ name: r.name, requiresCompetency: r.requiresCompetency })),
      ];
      let hasRoles = rolesToPersist.length > 0;

      if (!hasRoles) {
        const savedRoles = await loadJobRolesWithFallback().catch(() => [] as { name: string; requiresCompetency: boolean }[]);
        hasRoles = Array.isArray(savedRoles) && savedRoles.length > 0;
      }

      const nomRes = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/nominate-company-admin`, { headers });
      const nomData = nomRes.ok
        ? ((await nomRes.json().catch(() => null)) as {
            hasNomination?: boolean;
            deferred?: boolean;
            nominationStepComplete?: boolean;
          } | null)
        : null;
      let hasNomination =
        nomData?.nominationStepComplete === true ||
        nomData?.hasNomination === true ||
        nomData?.deferred === true ||
        hasNominationRecord ||
        nominationDeferred;

      const onboardingIssues = {
        missingJobRoles: !hasRoles,
        missingFramework: false,
        missingCompanyAdminNomination: !hasNomination,
        missingRoleMappings: false,
        missingCompanyLogo: !logoUrl,
      };
      if (onboardingIssues.missingCompanyAdminNomination || onboardingIssues.missingJobRoles) {
        const req: string[] = [];
        if (onboardingIssues.missingJobRoles) req.push("add at least one job role");
        if (onboardingIssues.missingCompanyAdminNomination)
          req.push("choose the company contact, nominate someone else, or add a company administrator later");
        setValidationIssues({
          ...onboardingIssues,
          activateError: `Complete required setup before activation: ${req.join("; ")}.`,
        } as typeof validationIssues & { activateError?: string });
        return;
      }
      if (rolesToPersist.length > 0) {
        await saveActivationPrerequisites(rolesToPersist);
      }
      const activateRes = azureOnly
        ? await (async () => {
            const headers: Record<string, string> = {
              "Content-Type": "application/json",
              "X-Company-Code": companyCode,
            };
            if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
            const apiRes = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/activate`, {
              method: "POST",
              headers,
              body: JSON.stringify({ userEmail: currentUserEmail }),
            });
            const apiData = await apiRes.json().catch(() => ({} as { success?: boolean; error?: string; nominatedAdminProvisioning?: unknown }));
            return apiRes.ok && apiData.success !== false
              ? ({ success: true as const, nominatedAdminProvisioning: apiData.nominatedAdminProvisioning } as const)
              : ({ success: false as const, error: apiData.error || "Failed to activate" } as const);
          })()
        : await activateCompanySetup({
            companyId: companyCode,
            userId: currentUserId,
            userEmail: currentUserEmail,
          });
      if (!activateRes.success) {
        setValidationIssues({ ...onboardingIssues, activateError: activateRes.error || "Failed to activate" } as typeof validationIssues & { activateError?: string });
        return;
      }
      const prov =
        "nominatedAdminProvisioning" in activateRes ? activateRes.nominatedAdminProvisioning : undefined;
      onActivated?.({
        companyName: companyDetails.name || company.name || company.companyCode || company.id,
        nominatedAdminEmail: nominatedAdminEmail.trim(),
        nominatedAdminName: nominatedAdminName.trim(),
        nominatedAdminJobTitle: nominatedAdminJobTitle.trim(),
        nominatedAdminProvisioning:
          typeof prov === "object" && prov !== null
            ? (prov as {
                provisioned?: boolean;
                emailed?: boolean;
                skipped?: boolean;
                warning?: string;
              })
            : undefined,
      });
    } catch (err) {
      const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Failed to activate";
      setValidationIssues((prev) => ({
        ...(prev || {
          missingJobRoles: false,
          missingFramework: false,
          missingCompanyAdminNomination: false,
          missingRoleMappings: false,
          missingCompanyLogo: false,
        }),
        activateError: msg,
      }));
    } finally {
      setValidating(false);
    }
  };

  const saveProgressBeforeLogout = React.useCallback(async () => {
    if (previewMode) return;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Company-Code": companyCode,
    };
    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;

    const allRoles = ensureRequiredOnboardingJobRoles(
      [
        ...selectedJobRoles,
        ...customRolesAdded.map((r) => ({ name: r.name, requiresCompetency: r.requiresCompetency })),
      ],
      lockInstructorRoles,
    );
    const toSaveMappings = roleMappings.filter((m) => m.jobRoleName && m.cycleId);

    const saveViaSQL = async () => {
      await saveCompanySetupWizardStep({
        companyId: companyCode,
        step: activeStep,
        userId: currentUserId,
        userEmail: currentUserEmail,
      });
      if (activeStep === "company-details" && !lockCompanyDetails && currentUserEmail) {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "X-User-Email": currentUserEmail,
          "X-Company-Code": companyCode,
        };
        const body = {
          companyCode,
          postalAddress: companyDetails.postalAddress.trim(),
          vatNumber: companyDetails.vatNumber.trim(),
          registeredAddressIfDifferent: companyDetails.registeredAddressIfDifferent.trim(),
          companyPhone: companyDetails.companyPhone.trim(),
          companyEmail: (companyDetails.contactEmail.trim() || companyDetails.companyEmail.trim() || String(currentUserEmail ?? "").trim()),
          contactName: companyDetails.contactName.trim(),
          operatorType: companyDetails.operatorType || undefined,
        };
        if (body.postalAddress && body.companyPhone && body.companyEmail && body.contactName && body.operatorType) {
          const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/company-details`, {
            method: "PATCH",
            headers,
            body: JSON.stringify(body),
          });
          if (!res.ok) throw new Error("Failed to save company details");
        }
      }
      if (activeStep === "job-roles" && allRoles.length > 0) {
        const res = await saveCompanyJobRoles({ companyId: companyCode, roles: allRoles, userId: currentUserId });
        if (!res.success) throw new Error(res.error || "Failed to save job roles");
      }
      if (activeStep === "role-mapping" && toSaveMappings.length > 0) {
        const res = await saveCompanyRoleMappings({
          companyId: companyCode,
          mappings: toSaveMappings,
          userId: currentUserId,
          userEmail: currentUserEmail,
        });
        if (!res.success) throw new Error(res.error || "Failed to save role mappings");
      }
    };

    const saveViaApiFallback = async () => {
      const stepRes = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/setup-wizard-step`, {
        method: "POST",
        headers,
        body: JSON.stringify({ step: activeStep }),
      });
      if (!stepRes.ok) throw new Error("Failed to save wizard step");
      if (activeStep === "company-details" && !lockCompanyDetails && currentUserEmail) {
        const body = {
          postalAddress: companyDetails.postalAddress.trim(),
          vatNumber: companyDetails.vatNumber.trim(),
          registeredAddressIfDifferent: companyDetails.registeredAddressIfDifferent.trim(),
          companyPhone: companyDetails.companyPhone.trim(),
          companyEmail: (companyDetails.contactEmail.trim() || companyDetails.companyEmail.trim() || String(currentUserEmail ?? "").trim()),
          contactName: companyDetails.contactName.trim(),
          operatorType: companyDetails.operatorType || undefined,
        };
        if (body.postalAddress && body.companyPhone && body.companyEmail && body.contactName && body.operatorType) {
          const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/company-details`, {
            method: "PATCH",
            headers,
            body: JSON.stringify(body),
          });
          if (!res.ok) throw new Error("Failed to save company details");
        }
      }
      if (activeStep === "job-roles" && allRoles.length > 0) {
        const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/job-roles`, {
          method: "POST",
          headers,
          body: JSON.stringify({ roles: allRoles }),
        });
        if (!res.ok) throw new Error("Failed to save job roles");
      }
      if (activeStep === "role-mapping" && toSaveMappings.length > 0) {
        const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/role-mappings`, {
          method: "POST",
          headers,
          body: JSON.stringify({ mappings: toSaveMappings }),
        });
        if (!res.ok) throw new Error("Failed to save role mappings");
      }
    };

    try {
      if (azureOnly) {
        await saveViaApiFallback();
      } else {
        await saveViaSQL();
      }
    } catch {
      await saveViaApiFallback();
    }
  }, [
    activeStep,
    companyCode,
    currentUserEmail,
    currentUserId,
    selectedJobRoles,
    customRolesAdded,
    roleMappings,
    lockCompanyDetails,
    companyDetails,
    azureOnly,
    previewMode,
  ]);

  const handleLogoutClick = async () => {
    if (!onLogout || logoutSaving) return;
    setLogoutError(null);
    setLogoutSaving(true);
    try {
      await saveProgressBeforeLogout();
      await Promise.resolve(onLogout());
    } catch (err: unknown) {
      const message = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Could not save onboarding progress before logout.";
      setLogoutError(`${message} Logging out anyway.`);
      await Promise.resolve(onLogout());
    } finally {
      setLogoutSaving(false);
    }
  };

  const roleNameExists = React.useCallback((name: string) => {
    const normalized = name.trim().toLowerCase();
    if (!normalized) return false;
    return (
      ALL_PREDEFINED_ROLES.some((r) => r.trim().toLowerCase() === normalized) ||
      customRolesAdded.some((r) => r.name.trim().toLowerCase() === normalized)
    );
  }, [customRolesAdded]);

  const addOtherRoleForSection = React.useCallback((sectionTitle: string) => {
    const raw = otherRoleInputBySection[sectionTitle] ?? "";
    const name = raw.trim();
    if (!name || roleNameExists(name)) return;
    setCustomRolesAdded((prev) => [...prev, { name, requiresCompetency: false, addedAt: new Date().toISOString(), section: sectionTitle }]);
    setOtherRoleInputBySection((prev) => ({ ...prev, [sectionTitle]: "" }));
  }, [otherRoleInputBySection, roleNameExists]);

  return (
      <div
        ref={wizardRootRef}
        className={cn(
          "rv-setup-wizard w-full min-w-0 max-w-full rounded-2xl sm:rounded-3xl shadow-sm overflow-x-hidden overflow-y-hidden flex flex-col",
          isDarkMode ? "rv-setup-wizard--dark bg-slate-900" : "bg-cover bg-center"
        )}
        style={isDarkMode ? { backgroundImage: "none", backgroundColor: "#0f172a" } : { backgroundImage: "url('/onboarding-bg.png')" }}
      >
        <style>{`
          ${isDarkMode ? SETUP_WIZARD_DARK_STYLES : ""}
          @keyframes rv-logo-upload-train-scroll {
            0% { left: 0; transform: translateY(-100%) translateX(-100%); }
            100% { left: 100%; transform: translateY(-100%) translateX(0); }
          }
          .rv-setup-wizard {
            box-sizing: border-box;
            width: 100%;
            max-width: 100%;
            min-width: 0;
            overflow-x: hidden;
          }
          .rv-setup-wizard .rv-setup-step {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            box-sizing: border-box;
          }
          .rv-setup-wizard .rv-setup-step-tabs {
            display: grid;
            width: 100%;
            max-width: 100%;
            min-width: 0;
            box-sizing: border-box;
            /* Phones: 4 columns (wraps to 2 rows). Wider: one row of equal steps. */
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.35rem;
          }
          @media (min-width: 641px) {
            .rv-setup-wizard .rv-setup-step-tabs {
              grid-template-columns: repeat(var(--rv-step-count, 7), minmax(0, 1fr));
            }
          }
          .rv-setup-wizard .rv-setup-step-tabs button {
            min-width: 0;
            max-width: 100%;
            width: 100%;
            justify-content: center;
            padding-left: 0.25rem;
            padding-right: 0.25rem;
            overflow: hidden;
            box-sizing: border-box;
          }
          .rv-setup-wizard .rv-terms-line {
            overflow-wrap: anywhere;
            word-break: break-word;
            max-width: 100%;
          }
          /* Prevent iOS focus-zoom from panning the fixed onboarding layout sideways. */
          .rv-setup-wizard input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]),
          .rv-setup-wizard textarea,
          .rv-setup-wizard select {
            font-size: 16px !important;
            max-width: 100%;
            min-width: 0;
            box-sizing: border-box;
          }
        `}</style>
        <div className="bg-white/60 backdrop-blur-[1px] min-w-0 max-w-full w-full overflow-x-hidden">
        {previewMode && (
          <div className="px-3 sm:px-6 lg:px-8 py-2 bg-indigo-50 border-b border-indigo-100 text-xs sm:text-sm text-indigo-900">
            {translateUi("Preview only — nothing is saved. Use Print this step or Print all steps above to export a PDF.")}
          </div>
        )}
        <div className="px-3 sm:px-6 lg:px-8 py-3 sm:py-6 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 sm:gap-3 bg-slate-50/90 min-w-0 w-full max-w-full">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img src="/rail-intel-logo-light.png?v=18" alt={translateUi("Rail Intel")} className="h-8 sm:h-10 w-auto max-w-[9rem] sm:max-w-[11rem] object-contain shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm sm:text-lg font-bold text-slate-900 truncate">
                {translateUi("Company Setup Wizard")}
              </h2>
              <p className="text-xs text-slate-500 truncate">
                {previewMode
                  ? translateUi("Preview only — nothing is saved.")
                  : (
                    <>
                      {translateUi("Complete onboarding for")} <span className="font-semibold">{company.name}</span>
                    </>
                  )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {onLogout && !previewMode && (
              <button
                type="button"
                onClick={handleLogoutClick}
                disabled={logoutSaving}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-rose-600 text-white text-xs font-semibold shadow-sm hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                <span>{logoutSaving ? "Saving…" : translateUi("Logout")}</span>
              </button>
            )}
            {!hideBackToDashboard && (
              <button
                type="button"
                onClick={onClose}
                className="text-xs text-slate-500 hover:text-slate-900 flex items-center space-x-1"
              >
                <LayoutDashboard className="w-3 h-3" />
                <span>{translateUi("Back to dashboard")}</span>
              </button>
            )}
          </div>
        </div>

        <div className={cn("flex flex-1 overflow-hidden min-w-0 max-w-full w-full", isMobileOrTablet ? "flex-col" : "flex-row")}>
          {/* Mobile/tablet: fixed equal-width step tabs that stay inside the card */}
          {isMobileOrTablet && (
          <div
            className="rv-setup-step-tabs w-full min-w-0 max-w-full p-2.5 sm:p-4 border-b border-slate-200 bg-slate-50/60 shrink-0"
            style={{ ["--rv-step-count"]: String(steps.length) } as React.CSSProperties}
          >
            {steps.map((step, index) => {
              const isActive = activeStep === step.id;
              const mustCompletePasswordFirst = contactNeedsPasswordChange && step.id !== "change-password";
              const mustAcceptTermsFirst =
                !previewMode &&
                !lockCompanyDetails &&
                !termsAccepted &&
                step.id !== "welcome" &&
                step.id !== "terms-and-conditions" &&
                step.id !== "change-password";
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => {
                    if (mustCompletePasswordFirst || mustAcceptTermsFirst) return;
                    setActiveStep(step.id);
                  }}
                  disabled={mustCompletePasswordFirst || mustAcceptTermsFirst}
                  title={translateUi(step.label)}
                  aria-label={`Step ${index + 1}: ${translateUi(step.label)}`}
                  className={`min-w-0 flex items-center justify-center gap-1 rounded-lg sm:rounded-xl text-xs font-medium min-h-[40px] sm:min-h-[44px] ${
                    isActive ? "bg-indigo-600 text-white" : (mustCompletePasswordFirst || mustAcceptTermsFirst) ? "text-slate-400" : "text-slate-600 bg-white border border-slate-200"
                  }`}
                >
                  <span className="w-5 h-5 rounded-full border border-current flex items-center justify-center text-[10px] shrink-0">{index + 1}</span>
                  <span className="hidden md:inline truncate">{translateUi(step.label)}</span>
                </button>
              );
            })}
          </div>
          )}
          <aside className={cn("w-56 border-r border-slate-200 bg-slate-50/60 p-4 space-y-3 shrink-0", isMobileOrTablet && "hidden")}>
            {steps.map((step, index) => {
              const isActive = activeStep === step.id;
              const mustCompletePasswordFirst = contactNeedsPasswordChange && step.id !== "change-password";
              const mustAcceptTermsFirst =
                !previewMode &&
                !lockCompanyDetails &&
                !termsAccepted &&
                step.id !== "welcome" &&
                step.id !== "terms-and-conditions" &&
                step.id !== "change-password";
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => {
                    if (mustCompletePasswordFirst || mustAcceptTermsFirst) return;
                    setActiveStep(step.id);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left text-xs font-medium ${
                    isActive
                      ? "bg-indigo-600 text-white"
                      : (mustCompletePasswordFirst || mustAcceptTermsFirst)
                        ? "text-slate-400 cursor-not-allowed"
                        : "text-slate-600 hover:bg-slate-100"
                  }`}
                  title={mustCompletePasswordFirst ? translateUi("Complete Change password first") : mustAcceptTermsFirst ? translateUi("Accept Terms & Conditions first") : undefined}
                >
                  <span className="flex items-center space-x-2">
                    <span className="w-5 h-5 rounded-full border border-current flex items-center justify-center text-[10px]">
                      {index + 1}
                    </span>
                    <span>{translateUi(step.label)}</span>
                  </span>
                </button>
              );
            })}
          </aside>

          <div
            ref={stepContentScrollRef}
            className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 lg:p-8 space-y-5 sm:space-y-6 min-h-0 min-w-0 max-w-full w-full overscroll-x-none"
          >
            {lockCompanyDetails && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3"> {translateUi("Company Details (locked)")} </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-1">{translateUi("Company name")}</p>
                    <div className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900">{companyDetails.name || company.name || company.companyCode || company.id || "—"}</div>
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-1">{translateUi("Company number")}</p>
                    <div className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900">{company.companyCode || company.id || "—"}</div>
                  </div>
                </div>
              </div>
            )}
            {contactNeedsPasswordChange && activeStep !== "change-password" && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
                <strong>{translateUi("Password change required.")}</strong> {translateUi("You must set a new password before continuing setup.")}
              </div>
            )}
            {logoutError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                <strong>{translateUi("Could not logout safely.")}</strong> {logoutError}
              </div>
            )}
            <div className="min-h-[320px] w-full min-w-0 max-w-full">
            {activeStep === "job-roles" ? (
              <div className="rv-setup-step space-y-5">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 min-w-0">
                  <Users className="w-5 h-5 text-indigo-600 shrink-0" />
                  <span className="min-w-0 break-words">{translateUi("Define your company&apos;s job roles")}</span>
                </h3>
                <p className="text-sm text-slate-500 break-words">
                  {translateUi("Select and name only the job roles that apply within your company that will be used on Rail Intel. These roles will be available when adding employees. You can choose from the sections below or create your own custom roles.")}
                </p>
                <div className="p-3 rounded-xl border border-sky-200 bg-sky-50 min-w-0">
                  <p className="text-xs sm:text-sm text-sky-900 break-words">
                    {lockInstructorRoles ? (
                      <>
                        <strong>{translateUi("Required for Trainee Driver:")}</strong>{" "}
                        {translateUi(
                          "Instructor and Senior Driver Instructor are locked on and cannot be removed. They are needed to allocate driving instructors to trainees.",
                        )}
                      </>
                    ) : (
                      <>
                        <strong>{translateUi("Optional instructor roles:")}</strong>{" "}
                        {translateUi(
                          "Based on your service answers, Instructor and Senior Driver Instructor are not locked. You can select them if needed.",
                        )}
                      </>
                    )}
                  </p>
                </div>
                <div className="p-3 rounded-xl border border-indigo-100 bg-indigo-50 min-w-0">
                  <p className="text-xs sm:text-sm text-indigo-800 break-words">
                    <strong>{translateUi("Competency cycle required?")}</strong> {translateUi("Turn this on for roles that must be assigned and tracked through a competency cycle.")}
                  </p>
                </div>
                <div className="p-3 sm:p-4 bg-red-100 border-2 border-red-300 rounded-xl min-w-0">
                  <p className="text-sm font-bold text-red-900 uppercase tracking-wide break-words">
                    {translateUi("Important: only select the job roles that apply to your company")}
                  </p>
                  <p className="text-sm text-red-900 mt-2 leading-relaxed normal-case font-normal break-words">
                    If you have a job role that is not listed, you can create your own by selecting{" "}
                    <strong> {translateUi("Other")} </strong> {translateUi("in each section below and typing the role name.")}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                  {JOB_ROLE_SECTIONS.map((section) => (
                    <div
                      key={section.title}
                      className={cn(
                        "rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow min-w-0",
                        section.fullWidth ? "sm:col-span-2" : ""
                      )}
                    >
                      <div className="px-4 py-2.5 bg-slate-100 border-b border-slate-200">
                        <h4 className="text-sm font-semibold text-slate-700 break-words">{translateUi(section.title)}</h4>
                      </div>
                      <div className="p-3 space-y-1.5">
                        {section.roles.map((role) => {
                          const current = selectedJobRoles.find((r) => r.name === role);
                          const isSelected = !!current;
                          const isRequired = isRequiredOnboardingJobRole(role, lockInstructorRoles);
                          return (
                            <label
                              key={role}
                              className={cn(
                                "flex flex-wrap items-center gap-2 sm:gap-3 p-2 rounded-lg transition-colors min-w-0",
                                isRequired ? "bg-sky-50/80 cursor-default" : "hover:bg-slate-50 cursor-pointer",
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected || isRequired}
                                disabled={isRequired}
                                onChange={(e) => {
                                  if (isRequired) return;
                                  if (e.target.checked) {
                                    setSelectedJobRoles((prev) =>
                                      ensureRequiredOnboardingJobRoles(
                                        [...prev, { name: role, requiresCompetency: false }],
                                        lockInstructorRoles,
                                      ),
                                    );
                                  } else {
                                    setSelectedJobRoles((prev) =>
                                      ensureRequiredOnboardingJobRoles(
                                        prev.filter((r) => r.name !== role),
                                        lockInstructorRoles,
                                      ),
                                    );
                                  }
                                }}
                                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0 disabled:opacity-80"
                              />
                              <span className="flex-1 min-w-0 text-xs font-medium text-slate-800 break-words">
                                {translateUi(role)}
                                {isRequired ? (
                                  <span className="ml-2 inline-flex items-center rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-800">
                                    {translateUi("Required")}
                                  </span>
                                ) : null}
                              </span>
                              {(isSelected || isRequired) && (
                                <label className="flex items-center gap-1.5 text-[11px] text-indigo-700 shrink-0 bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1">
                                  <input
                                    type="checkbox"
                                    checked={current?.requiresCompetency ?? false}
                                    onChange={(e) => {
                                      setSelectedJobRoles((prev) =>
                                        ensureRequiredOnboardingJobRoles(
                                          prev.some((r) => r.name === role)
                                            ? prev.map((r) =>
                                                r.name === role ? { ...r, requiresCompetency: e.target.checked } : r,
                                              )
                                            : [...prev, { name: role, requiresCompetency: e.target.checked }],
                                          lockInstructorRoles,
                                        ),
                                      );
                                    }}
                                    className="w-3 h-3 rounded border-slate-300 text-indigo-600"
                                  />
                                  {translateUi("Requires cycle")}
                                </label>
                              )}
                            </label>
                          );
                        })}
                        <div className="mt-2 p-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 space-y-2">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!otherRoleEnabledBySection[section.title]}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setOtherRoleEnabledBySection((prev) => ({ ...prev, [section.title]: checked }));
                                if (!checked) {
                                  setOtherRoleInputBySection((prev) => ({ ...prev, [section.title]: "" }));
                                }
                              }}
                              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                            />
                            <span className="text-xs font-semibold text-slate-700"> {translateUi("Other")} </span>
                          </label>
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              type="text"
                              value={otherRoleInputBySection[section.title] ?? ""}
                              onChange={(e) => setOtherRoleInputBySection((prev) => ({ ...prev, [section.title]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addOtherRoleForSection(section.title);
                                }
                              }}
                              disabled={!otherRoleEnabledBySection[section.title]}
                              placeholder={`Type custom role for ${section.title}`}
                              className="px-3 py-2 border border-slate-200 rounded-lg text-xs w-full focus:ring-2 focus:ring-indigo-500 outline-none bg-white disabled:bg-slate-100 disabled:text-slate-400"
                            />
                            <button
                              type="button"
                              onClick={() => addOtherRoleForSection(section.title)}
                              disabled={!otherRoleEnabledBySection[section.title] || !(otherRoleInputBySection[section.title] ?? "").trim() || roleNameExists(otherRoleInputBySection[section.title] ?? "")}
                              className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            > {translateUi("Add")} </button>
                          </div>
                          {otherRoleEnabledBySection[section.title] && roleNameExists(otherRoleInputBySection[section.title] ?? "") && (otherRoleInputBySection[section.title] ?? "").trim() && (
                            <p className="text-[11px] text-amber-700 mt-1">{translateUi("Role already exists.")}</p>
                          )}
                          {customRolesAdded.filter((r) => r.section === section.title).length > 0 && (
                            <div className="pt-2 border-t border-slate-200 space-y-1.5">
                              <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{translateUi("Added custom roles")}</p>
                              {customRolesAdded.filter((r) => r.section === section.title).map((r) => (
                                <div key={`${section.title}-${r.name}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                                  <span className="flex-1 text-xs font-medium text-slate-800">{r.name}</span>
                                  <label className="flex items-center gap-1 text-[11px] text-indigo-700 shrink-0 bg-indigo-50 border border-indigo-200 rounded-md px-1.5 py-1">
                                    <input
                                      type="checkbox"
                                      checked={r.requiresCompetency}
                                      onChange={(e) => {
                                        setCustomRolesAdded((prev) =>
                                          prev.map((x) =>
                                            x.name === r.name && x.section === section.title
                                              ? { ...x, requiresCompetency: e.target.checked }
                                              : x
                                          )
                                        );
                                      }}
                                      className="w-3 h-3 rounded border-slate-300 text-indigo-600"
                                    />
                                    {translateUi("Requires cycle")}
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setCustomRolesAdded((prev) => prev.filter((x) => !(x.name === r.name && x.section === section.title)))}
                                    className="text-red-500 hover:text-red-700 font-bold text-sm px-1"
                                    aria-label={translateUi("Remove custom role {{name}}", { name: r.name })}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {jobRolesError && (
                  <p className="text-sm text-red-600">{jobRolesError}</p>
                )}
                <div className="flex flex-col items-end gap-2 pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center w-full gap-3 flex-wrap">
                  <span className="text-sm text-slate-500">
                    {selectedJobRoles.length + customRolesAdded.length} role{(selectedJobRoles.length + customRolesAdded.length) !== 1 ? "s" : ""} selected
                  </span>
                  <button
                    type="button"
                    disabled={(selectedJobRoles.length === 0 && customRolesAdded.length === 0) || jobRolesSaving}
                    onClick={async () => {
                      const allRoles = ensureRequiredOnboardingJobRoles(
                        [
                          ...selectedJobRoles,
                          ...customRolesAdded.map((r) => ({ name: r.name, requiresCompetency: r.requiresCompetency })),
                        ],
                        lockInstructorRoles,
                      );
                      if (allRoles.length === 0) return;
                      if (previewMode) {
                        if (!editorMode) setActiveStep("addons");
                        return;
                      }
                      setJobRolesError(null);
                      setJobRolesSaving(true);
                      try {
                        const headers: Record<string, string> = {
                          "Content-Type": "application/json",
                          "X-Company-Code": companyCode,
                        };
                        if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
                        const apiRes = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/job-roles`, {
                          method: "POST",
                          headers,
                          body: JSON.stringify({ roles: allRoles }),
                        });
                        if (apiRes.ok) {
                          setJobRolesSaving(false);
                          markStepSaved("job-roles");
                          if (!editorMode) setActiveStep("addons");
                          return;
                        }
                        if (azureOnly) {
                          setJobRolesSaving(false);
                          setJobRolesError("Failed to save job roles");
                          return;
                        }
                        const fsRes = await saveCompanyJobRoles({
                          companyId: companyCode,
                          roles: allRoles,
                          userId: currentUserId,
                        });
                        setJobRolesSaving(false);
                        if (fsRes.success) {
                          setActiveStep("addons");
                        } else {
                          // Do not block onboarding if sync fails; local draft is already persisted.
                          setActiveStep("addons");
                        }
                      } catch {
                        setJobRolesSaving(false);
                        if (!editorMode) setActiveStep("addons");
                      }
                    }}
                    className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {jobRolesSaving ? "Saving…" : editorMode ? translateUi("Save changes") : translateUi("Save & continue")}
                  </button>
                  </div>
                  <RequiredFieldsHelper
                    missing={
                      selectedJobRoles.length === 0 && customRolesAdded.length === 0
                        ? ["Select or add at least one job role"]
                        : []
                    }
                  />
                  {editorMode && savedNoticeByStep["job-roles"] && (
                    <span className="text-xs font-bold text-emerald-700">{savedNoticeByStep["job-roles"]}</span>
                  )}
                </div>
              </div>
            ) : (
            <>
              {activeStep === "change-password" && (
                <div key="change-password" className="rv-setup-step space-y-6">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-indigo-600" />
                    {translateUi("Change your password")}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {translateUi("You signed in with a temporary password. Set a new password to continue.")}
                  </p>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setPasswordError(null);
                      const { newPassword, confirmPassword } = passwordForm;
                      if (!newPassword || newPassword.length < 8) {
                        setPasswordError("Password must be at least 8 characters");
                        return;
                      }
                      if (newPassword !== confirmPassword) {
                        setPasswordError(translateUi("Passwords do not match"));
                        return;
                      }
                      setPasswordLoading(true);
                      try {
                        if (!currentUserEmail) {
                          setPasswordError("Not signed in");
                          setPasswordLoading(false);
                          return;
                        }
                        const res = await fetch("/api/users/change-password", {
                          method: "POST",
                          headers: { "Content-Type": "application/json", "X-User-Email": currentUserEmail },
                          body: JSON.stringify({
                            email: currentUserEmail,
                            currentPassword: "", // temp already consumed at login; allow set when hash exists via admin
                            newPassword,
                            allowWithoutCurrent: true,
                          }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(data.error || "Failed to update password");
                        if (currentUserEmail) {
                          const passwordChangedRes = await fetch("/api/users/me/password-changed", {
                            method: "POST",
                            headers: { "Content-Type": "application/json", "X-User-Email": currentUserEmail },
                            body: JSON.stringify({ companyCode }),
                          });
                          if (!passwordChangedRes.ok) {
                            console.warn("[SetupWizard] password change status update failed");
                          }
                        }
                        // Best-effort SQL sync only (disabled for Azure SQL-only system-admin editor).
                        if (!azureOnly) {
                          try {
                            await markCompanyContactPasswordChanged({
                              companyId: companyCode,
                              userId: currentUserId,
                              userEmail: currentUserEmail,
                            });
                          } catch (syncErr) {
                            console.warn("[SetupWizard] password-change SQL sync skipped:", syncErr);
                          }
                        }
                        setContactNeedsPasswordChange(false);
                        setPasswordForm({ newPassword: "", confirmPassword: "" });
                        setActiveStep(lockCompanyDetails ? "job-roles" : (termsAccepted ? "company-details" : "welcome"));
                      } catch (err: unknown) {
                        const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Failed to update password";
                        setPasswordError(msg);
                      } finally {
                        setPasswordLoading(false);
                      }
                    }}
                    className="space-y-4 w-full max-w-2xl"
                  >
                    {passwordError && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{passwordError}</div>
                    )}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{translateUi("New password")}</label>
                      <input
                        type="password"
                        required
                        minLength={8}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="At least 8 characters"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{translateUi("Confirm new password")}</label>
                      <input
                        type="password"
                        required
                        minLength={8}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="Repeat password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {passwordLoading ? translateUi("Updating…") : translateUi("Set new password & continue")}
                    </button>
                  </form>
                </div>
              )}
              {activeStep === "welcome" && (
                <div key="welcome" className="rv-setup-step w-full max-w-5xl mx-auto space-y-6 sm:space-y-8">
                  {/* Hero */}
                  <section className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-slate-200/90 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white shadow-xl">
                    <div
                      className="pointer-events-none absolute top-0 right-0 h-40 w-40 rounded-full bg-sky-400/20 blur-3xl"
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 left-0 h-44 w-44 rounded-full bg-indigo-500/25 blur-3xl"
                      aria-hidden
                    />
                    <div className="relative z-[1] p-5 sm:p-8 lg:p-10 space-y-5 sm:space-y-6">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
                        <div className="shrink-0 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl bg-white border border-white/20 shadow-lg overflow-hidden">
                          <img
                            src="/rail-intel-icon.png?v=18"
                            alt={translateUi("Rail Intel")}
                            className="h-11 w-11 sm:h-12 sm:w-12 object-contain"
                          />
                        </div>
                        <div className="min-w-0 flex-1 space-y-3">
                          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-sky-200/90">
                            {translateUi("Rail Intel · Competency management")}
                          </p>
                          <h3 className="text-2xl sm:text-3xl lg:text-[2.15rem] font-bold tracking-tight leading-tight">
                            {translateUi("Welcome to Rail Intel")}
                          </h3>
                          <p className="text-sm sm:text-base text-slate-200/95 leading-relaxed max-w-3xl">
                            You&apos;ve signed up to the UK rail competency platform built for operators who need clear
                            assurance — who is competent, when renewals are due, and how assessments, medicals, and
                            operational records stay audit-ready in one system.
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
                        {[
                          { label: "Competency cycles", detail: "Frameworks, criteria & expiry" },
                          { label: "Workforce records", detail: "Roles, medicals & evidence" },
                          { label: "Assurance ready", detail: "Clear audit trails for reviews" },
                          { label: "Company control", detail: "Admins, permissions & standards" },
                        ].map((chip) => (
                          <div
                            key={chip.label}
                            className="rounded-xl border border-white/15 bg-white/10 px-3.5 py-3 backdrop-blur-sm"
                          >
                            <p className="text-sm font-semibold text-white">{chip.label}</p>
                            <p className="text-xs text-slate-300 mt-0.5 leading-snug">{chip.detail}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* Core platform */}
                  <section className="space-y-3 sm:space-y-4">
                    <div className="space-y-1">
                      <h4 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                        {translateUi("What&apos;s included in your platform")}
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
                        Core Rail Intel covers the competency lifecycle for{" "}
                        <strong className="font-semibold text-slate-800">
                          {companyDetails.name || company.name || company.companyCode || company.id || "your company"}
                        </strong>
                        . This short onboarding gets your company live; administrators then refine frameworks, grading,
                        and standards under Administration.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                      {[
                        {
                          title: "People & roles",
                          body: "Hold employee records, job roles, and access levels so the right people see the right data — from drivers and assessors to managers.",
                          Icon: Users,
                          accent: "bg-indigo-50 text-indigo-700 ring-indigo-100",
                        },
                        {
                          title: "Competency & cycles",
                          body: "Apply competency frameworks and cycles, track assessments against criteria, and surface expiry so renewals never rely on spreadsheets alone.",
                          Icon: Award,
                          accent: "bg-sky-50 text-sky-800 ring-sky-100",
                        },
                        {
                          title: "Medicals & fitness",
                          body: "Record medical status and related evidence alongside competency, giving managers a joined-up view of fitness to operate.",
                          Icon: Stethoscope,
                          accent: "bg-emerald-50 text-emerald-800 ring-emerald-100",
                        },
                        {
                          title: "Incidents & follow-up",
                          body: "Capture incidents and related actions in context, supporting investigation workflows and organisational learning.",
                          Icon: AlertTriangle,
                          accent: "bg-amber-50 text-amber-900 ring-amber-100",
                        },
                        {
                          title: "Reporting & assurance",
                          body: "Use dashboards and records that make compliance visible — who is competent, what is outstanding, and what evidence sits behind decisions.",
                          Icon: BarChart3,
                          accent: "bg-cyan-50 text-cyan-900 ring-cyan-100",
                        },
                        {
                          title: "Traction & routes",
                          body: "Maintain traction and route knowledge aligned to your operation, so competence stays tied to how your railway actually runs.",
                          Icon: TrainFront,
                          accent: "bg-slate-100 text-slate-800 ring-slate-200",
                        },
                      ].map((item) => (
                        <div
                          key={item.title}
                          className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all"
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={cn(
                                "shrink-0 flex h-10 w-10 items-center justify-center rounded-xl ring-1",
                                item.accent
                              )}
                            >
                              <item.Icon className="w-5 h-5" aria-hidden />
                            </div>
                            <div className="min-w-0 space-y-1.5">
                              <p className="text-sm sm:text-base font-bold text-slate-900">{item.title}</p>
                              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{item.body}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Add-ons marketing */}
                  <section
                    className={cn(
                      "relative overflow-hidden rounded-2xl sm:rounded-3xl border p-4 sm:p-6 lg:p-7 space-y-4 sm:space-y-5",
                      isDarkMode
                        ? "border-slate-600 bg-slate-900/90"
                        : "border-slate-200/90 bg-gradient-to-br from-slate-50 via-sky-50/40 to-indigo-50/50"
                    )}
                  >
                    {!isDarkMode && (
                      <>
                    <div
                      className="pointer-events-none absolute top-0 right-0 h-40 w-40 rounded-full bg-sky-200/35 blur-3xl"
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 left-0 h-44 w-44 rounded-full bg-indigo-200/30 blur-3xl"
                      aria-hidden
                    />
                      </>
                    )}
                    <div className="relative z-[1] flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                      <div className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-lg shadow-indigo-200/50">
                        <LayoutDashboard className="w-5 h-5" aria-hidden />
                      </div>
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                            {translateUi("Extend with bolt-on modules")}
                          </h4>
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                              isDarkMode ? "bg-indigo-500/25 text-indigo-200" : "bg-indigo-100 text-indigo-800"
                            )}
                          > {translateUi("Optional")} </span>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">
                          Grow beyond core competency into a fuller operational suite. Activate modules during onboarding
                          or later from Administration → Add-ons — only pay for what your operation needs.
                        </p>
                      </div>
                    </div>
                    <div className="relative z-[1] grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                      {onboardingAddons.map((mod) => {
                        const accentRing =
                          mod.accent === "violet"
                            ? "bg-violet-500/15 text-violet-700 ring-violet-200/60"
                            : mod.accent === "emerald"
                              ? "bg-emerald-500/15 text-emerald-700 ring-emerald-200/60"
                              : mod.accent === "cyan"
                                ? "bg-cyan-500/15 text-cyan-800 ring-cyan-200/60"
                                : mod.accent === "amber"
                                  ? "bg-amber-500/15 text-amber-900 ring-amber-200/60"
                                  : "bg-indigo-500/15 text-indigo-700 ring-indigo-200/60";
                        const Icon =
                          mod.id === "task-assignment"
                            ? ClipboardList
                            : mod.id === "driver-reports"
                              ? FileCheck
                              : mod.id === "leave-absence"
                                ? Calendar
                                : mod.id === "trainee-driver"
                                  ? GraduationCap
                                  : mod.id === "safety-briefs"
                                    ? Megaphone
                                    : Shield;
                        return (
                          <article
                            key={mod.id}
                            className={cn(
                              "flex h-full flex-col gap-3 rounded-2xl border p-4 sm:p-5 shadow-sm backdrop-blur-sm transition-all",
                              isDarkMode
                                ? "border-slate-600 bg-slate-800/90 hover:border-indigo-400/50"
                                : "border-slate-200/90 bg-white/90 hover:shadow-md hover:border-indigo-200/80"
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={cn(
                                  "shrink-0 flex h-10 w-10 items-center justify-center rounded-xl ring-1",
                                  accentRing
                                )}
                              >
                                <Icon className="w-5 h-5" aria-hidden />
                              </div>
                              <div className="min-w-0 space-y-1">
                                <p className="text-sm sm:text-base font-bold text-slate-900">{mod.name}</p>
                                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                                  {mod.shortDescription}
                                </p>
                              </div>
                            </div>
                            <div
                              className={cn(
                                "mt-auto rounded-xl border px-3 py-2.5",
                                isDarkMode ? "border-slate-600 bg-slate-900/70" : "border-slate-100 bg-slate-50/90"
                              )}
                            >
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                                {translateUi("Why operators add this")}
                              </p>
                              <p className="text-xs text-slate-700 leading-relaxed">{mod.howItHelps}</p>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                    <p className="relative z-[1] text-xs sm:text-sm text-slate-500 leading-relaxed">
                      You&apos;ll choose which modules to activate later in this wizard. Nothing is billed until you
                      activate or purchase an add-on.
                    </p>
                  </section>

                  {/* Next steps */}
                  <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4 shadow-sm">
                    <div className="space-y-1">
                      <h4 className="text-base sm:text-lg font-bold text-slate-900">{translateUi("Ready to begin onboarding?")}</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Next you&apos;ll accept the Terms &amp; Conditions, confirm company details, set job roles,
                        optionally enable add-ons, and choose your company administrator for{" "}
                        <strong className="font-semibold text-slate-800">
                          {companyDetails.name || company.name || company.companyCode || company.id || "your company"}
                        </strong>
                        .
                      </p>
                    </div>

                    <fieldset className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4">
                      <legend className="px-1 text-sm font-semibold text-slate-900">
                        {translateUi("What type of rail services does your company provide?")}
                      </legend>
                      <div className="flex flex-col gap-2">
                        {(
                          [
                            { value: "passenger" as const, label: "Passenger services" },
                            { value: "freight" as const, label: "Freight services" },
                            { value: "other" as const, label: "Other" },
                          ] as const
                        ).map((opt) => (
                          <label
                            key={opt.value}
                            className={cn(
                              "flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 cursor-pointer transition-colors",
                              railServiceType === opt.value
                                ? "border-indigo-400 ring-1 ring-indigo-200"
                                : "border-slate-200 hover:border-slate-300",
                            )}
                          >
                            <input
                              type="radio"
                              name="rail-service-type"
                              value={opt.value}
                              checked={railServiceType === opt.value}
                              onChange={() => {
                                setRailServiceType(opt.value);
                                setTrainsNewDrivers(opt.value === "other" ? trainsNewDrivers : null);
                                setRailServiceError(null);
                              }}
                              className="w-4 h-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm font-medium text-slate-800">{translateUi(opt.label)}</span>
                          </label>
                        ))}
                      </div>

                      {railServiceType === "other" ? (
                        <div className="space-y-2 pt-1 border-t border-slate-200">
                          <p className="text-sm font-semibold text-slate-900">
                            {translateUi("Would they be involved in training new train drivers?")}
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2">
                            {(
                              [
                                { value: true, label: "Yes" },
                                { value: false, label: "No" },
                              ] as const
                            ).map((opt) => (
                              <label
                                key={String(opt.value)}
                                className={cn(
                                  "flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 cursor-pointer transition-colors sm:flex-1",
                                  trainsNewDrivers === opt.value
                                    ? "border-indigo-400 ring-1 ring-indigo-200"
                                    : "border-slate-200 hover:border-slate-300",
                                )}
                              >
                                <input
                                  type="radio"
                                  name="trains-new-drivers"
                                  checked={trainsNewDrivers === opt.value}
                                  onChange={() => {
                                    setTrainsNewDrivers(opt.value);
                                    setRailServiceError(null);
                                  }}
                                  className="w-4 h-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-sm font-medium text-slate-800">{translateUi(opt.label)}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            {trainsNewDrivers === true
                              ? translateUi(
                                  "Instructor and Senior Driver Instructor will be locked as required job roles.",
                                )
                              : trainsNewDrivers === false
                                ? translateUi(
                                    "Instructor and Senior Driver Instructor will not be locked or selected by default.",
                                  )
                                : translateUi("Select Yes or No to continue.")}
                          </p>
                        </div>
                      ) : railServiceType === "passenger" || railServiceType === "freight" ? (
                        <p className="text-xs text-slate-500 leading-relaxed">
                          {translateUi(
                            "Instructor and Senior Driver Instructor will be locked as required job roles for trainee driver allocation.",
                          )}
                        </p>
                      ) : null}
                    </fieldset>

                    {railServiceError ? (
                      <p className="text-sm text-red-600" role="alert">
                        {railServiceError}
                      </p>
                    ) : null}

                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <button
                        type="button"
                        disabled={!railServiceAnswered || railServiceSaving}
                        onClick={async () => {
                          if (!railServiceAnswered) {
                            setRailServiceError(translateUi("Please answer the service type question before continuing."));
                            return;
                          }
                          if (previewMode) {
                            setActiveStep("terms-and-conditions");
                            return;
                          }
                          setRailServiceSaving(true);
                          setRailServiceError(null);
                          try {
                            await persistRailServiceAnswers();
                            setActiveStep("terms-and-conditions");
                          } catch (err: unknown) {
                            setRailServiceError(
                              err instanceof Error ? err.message : translateUi("Failed to save service type"),
                            );
                          } finally {
                            setRailServiceSaving(false);
                          }
                        }}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 shadow-lg shadow-indigo-200/50 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
                      >
                        {railServiceSaving
                          ? translateUi("Saving…")
                          : translateUi("Next: Terms & Conditions")}
                        {!railServiceSaving ? <ChevronRight className="w-4 h-4" aria-hidden /> : null}
                      </button>
                      <p className="text-xs text-slate-500 sm:max-w-xs leading-relaxed">
                        {translateUi("Takes a few minutes. You can save progress and return if needed.")}
                      </p>
                    </div>
                  </section>
                </div>
              )}
              {activeStep === "terms-and-conditions" && (
                <div key="terms-and-conditions" className="rv-setup-step space-y-6">
                  <h3 className="text-lg font-bold text-slate-900">
                    {translateUi("Terms & Conditions")}
                  </h3>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5 space-y-3">
                    <p className="text-sm text-slate-700">
                      {translateUi("Before continuing, please read the Terms and Conditions below. The acceptance checkbox remains disabled until you scroll to the bottom.")}
                    </p>
                    <div
                      ref={termsScrollContainerRef}
                      onScroll={handleTermsScroll}
                      className="max-h-[48rem] overflow-y-auto overflow-x-hidden overscroll-x-none rounded-xl border border-slate-200 bg-white p-3 sm:p-4 space-y-4 min-w-0 max-w-full"
                    >
                      <div className="text-xs sm:text-sm text-slate-700 leading-relaxed font-sans space-y-1.5 min-w-0 max-w-full break-words">
                        {renderTermsScrollContent(termsSignerName, termsSignerJobTitle, termsSignatureDataUrl, termsMarkdown)}
                      </div>
                    </div>
                    <p className={`text-xs font-medium ${termsScrolledToBottom ? "text-emerald-700" : "text-amber-700"}`}>
                      {termsScrolledToBottom
                        ? translateUi("Helper: You have reached the bottom. Acceptance is now enabled.")
                        : translateUi("Helper: Scroll to the bottom of the terms to enable acceptance.")}
                    </p>
                    <label className={`flex items-start gap-3 rounded-xl border bg-white p-4 ${termsScrolledToBottom ? "border-slate-200 cursor-pointer" : "border-slate-200/70 opacity-70 cursor-not-allowed"}`}>
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        disabled={!termsScrolledToBottom}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setTermsAccepted(checked);
                          if (checked) {
                            setTermsAgreementStampedAt(new Date().toISOString());
                          } else {
                            setTermsAgreementStampedAt(null);
                          }
                        }}
                        className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="min-w-0 flex-1 space-y-1">
                        <span className="block text-sm text-slate-700">
                          {translateUi("I confirm that I have read, understood, and accept Rail Intel Ltd&apos;s Terms and Conditions.")}
                        </span>
                        {termsAccepted && termsAgreementStampedAt && (
                          <p className="text-xs text-slate-500">
                            Acceptance recorded at {formatUkDateTime(new Date(termsAgreementStampedAt))} (UK time).
                          </p>
                        )}
                      </div>
                    </label>
                    <div
                      ref={termsSignatureSectionRef}
                      className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 scroll-mt-4"
                    >
                      <p className="text-sm font-semibold text-slate-900">{translateUi("Electronic signature details")}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="space-y-1">
                          <span className="text-xs font-medium text-slate-600">{translateUi("Print name:")}<RequiredMark /></span>
                          <input
                            type="text"
                            value={termsSignerName}
                            onChange={(e) => setTermsSignerName(e.target.value)}
                            onFocus={scheduleScrollTermsSignatureForFormFocus}
                            placeholder="Enter full name:"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-medium text-slate-600">{translateUi("Job title")}<RequiredMark /></span>
                          <input
                            type="text"
                            value={termsSignerJobTitle}
                            onChange={(e) => setTermsSignerJobTitle(e.target.value)}
                            onFocus={scheduleScrollTermsSignatureForFormFocus}
                            placeholder="Enter job title"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 font-serif font-bold text-sm text-slate-900 placeholder:font-sans placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </label>
                      </div>
                      <TermsSignaturePad
                        onSignatureChange={setTermsSignatureDataUrl}
                      />
                      <p className="text-xs text-slate-500">
                        {translateUi("Your print name and signature appear next to &quot;Signed electronically by (Customer)&quot; in the terms above as you enter them. Acceptance records include signer identity, job title, signature, and timestamp for audit evidence.")}
                      </p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          disabled={!termsRecordDetailsReady}
                          title={
                            termsRecordDetailsReady
                              ? translateUi("Download an HTML file you can keep or archive")
                              : translateUi("Enter print name, job title, and sign above to download a copy")
                          }
                          onClick={handleDownloadTermsAgreementRecord}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50 shadow-sm disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
                        >
                          <FileCheck className="w-4 h-4 shrink-0 text-indigo-600" aria-hidden /> {translateUi("Download copy (HTML)")} </button>
                        <button
                          type="button"
                          disabled={!termsRecordDetailsReady}
                          title={
                            termsRecordDetailsReady ? translateUi("Opens your browser print dialog — choose Save as PDF if you prefer") : translateUi("Enter print name, job title, and sign above to print")
                          }
                          onClick={handlePrintTermsAgreementRecord}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50 shadow-sm disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
                        >
                          <Printer className="w-4 h-4 shrink-0 text-indigo-600" aria-hidden />
                          {translateUi("Print / PDF")}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Auto-detected external IP: {requesterExternalIpAddress}
                      </p>
                      {termsAcceptedAt && (
                        <p className="text-xs text-emerald-700">
                          Accepted on {new Date(termsAcceptedAt).toLocaleString()} by {termsAcceptedBy || currentUserEmail || "current user"}.
                        </p>
                      )}
                    </div>
                    {termsAcceptanceError && (
                      <p className="text-sm text-rose-600">{termsAcceptanceError}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={
                      !termsAccepted ||
                      !termsSignerName.trim() ||
                      !termsSignerJobTitle.trim() ||
                      !termsSignatureDataUrl ||
                      termsAcceptanceSaving
                    }
                    onClick={async () => {
                      setTermsAcceptanceError(null);
                      if (previewMode) {
                        setTermsAccepted(true);
                        setTermsAcceptedAt(new Date().toISOString());
                        if (!editorMode) setActiveStep("company-details");
                        return;
                      }
                      if (!currentUserEmail) {
                        setTermsAcceptanceError("Your session must include an email to save terms acceptance.");
                        return;
                      }
                      if (!termsSignerName.trim()) {
                        setTermsAcceptanceError("Enter your print name before accepting the Terms.");
                        return;
                      }
                      if (!termsSignerJobTitle.trim()) {
                        setTermsAcceptanceError("Enter your job title before accepting the Terms.");
                        return;
                      }
                      if (!termsSignatureDataUrl) {
                        setTermsAcceptanceError("Sign in the signature box before accepting the Terms.");
                        return;
                      }
                      setTermsAcceptanceSaving(true);
                      try {
                        const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/terms-acceptance`, {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            "X-User-Email": currentUserEmail,
                            "X-Company-Code": companyCode,
                          },
                          body: JSON.stringify({
                            companyCode,
                            signerName: termsSignerName.trim(),
                            signerEmail: currentUserEmail.trim(),
                            signerJobTitle: termsSignerJobTitle.trim(),
                            signatureDataUrl: termsSignatureDataUrl,
                            acceptanceMethod: "tick-box",
                          }),
                        });
                        const data = await res.json().catch(() => ({} as { error?: string; termsAcceptedAt?: string; termsAcceptedBy?: string }));
                        if (!res.ok) throw new Error(data.error || "Failed to save terms acceptance");
                        if (data.termsAcceptedAt) setTermsAcceptedAt(data.termsAcceptedAt);
                        if (data.termsAcceptedBy) setTermsAcceptedBy(data.termsAcceptedBy);
                        setActiveStep("company-details");
                      } catch (err) {
                        const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Failed to save terms acceptance";
                        setTermsAcceptanceError(msg);
                      } finally {
                        setTermsAcceptanceSaving(false);
                      }
                    }}
                    className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {termsAcceptanceSaving ? translateUi("Saving…") : translateUi("Continue to company details →")}
                  </button>
                  <RequiredFieldsHelper
                    className="mt-2"
                    missing={[
                      ...(!termsScrolledToBottom ? ["Scroll to the bottom of the Terms"] : []),
                      ...(!termsAccepted ? ["Tick the acceptance checkbox"] : []),
                      ...(!termsSignerName.trim() ? ["Enter your print name"] : []),
                      ...(!termsSignerJobTitle.trim() ? ["Enter your job title"] : []),
                      ...(!termsSignatureDataUrl ? ["Add your electronic signature"] : []),
                    ]}
                  />
                </div>
              )}
              {activeStep === "company-details" && (
                <div key="company-details" className="rv-setup-step space-y-6">
                  <h3 className="text-lg font-bold text-slate-900">
                    {translateUi("Company Details")}
                  </h3>
                  {lockCompanyDetails ? (
                    <>
                      <p className="text-sm text-slate-500 flex items-center gap-2">
                        <Lock className="w-4 h-4 text-slate-400" />
                        {translateUi("These details were set by your System Administrator and cannot be changed here.")}
                      </p>
                      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 min-w-0">
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Company name")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">{companyDetails.name || "—"}</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider"> {translateUi("VAT number (optional)")} </label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">{companyDetails.vatNumber || "—"}</p>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Full postal address")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2 min-h-[60px] whitespace-pre-wrap">{companyDetails.postalAddress || "—"}</p>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider"> {translateUi("Registered company address (if different)")} </label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2 min-h-[60px] whitespace-pre-wrap">{companyDetails.registeredAddressIfDifferent || "—"}</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Company contact number")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">{companyDetails.companyPhone || "—"}</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Company contact email")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">{companyDetails.companyEmail || companyDetails.contactEmail || "—"}</p>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Operator type")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">
                            {companyDetails.operatorType === "railway_undertaking"
                              ? "Railway Undertaking (TOC or FOC)"
                              : companyDetails.operatorType === "infrastructure_manager"
                                ? "Infrastructure Manager"
                                : "—"}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Company contact full name")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">{companyDetails.contactName || "—"}</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Company contact email")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">{companyDetails.contactEmail || "—"}</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Primary contact telephone")}</label>
                          <p className="text-sm text-slate-900 bg-white border border-slate-200 rounded-xl px-4 py-2">{companyDetails.contactPhone || "—"}</p>
                        </div>
                      </div>
                      {logoUrl && (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{translateUi("Company logo")}</p>
                          <div className="w-20 h-20 border border-slate-200 rounded-xl overflow-hidden bg-white">
                            <img src={logoUrl} alt={translateUi("Company logo")} className="w-full h-full object-contain" />
                          </div>
                        </div>
                      )}
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={() => setActiveStep("job-roles")}
                          className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700"
                        >
                          {translateUi("Continue to job roles →")}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-500">
                        {translateUi("Provide your company&apos;s registered details. This information is stored securely in Azure SQL and is used for your complementary certificate (Part B) and company records.")}
                      </p>
                      <p
                        className={cn(
                          "text-sm rounded-xl border px-4 py-3",
                          isDarkMode
                            ? "text-slate-200 border-amber-500/40 bg-amber-950/45"
                            : "text-slate-600 border-amber-200 bg-amber-50/80"
                        )}
                      >
                        <span className={cn("font-semibold", isDarkMode ? "text-amber-100" : "text-slate-800")}>
                          {translateUi("Train Driver Licencing (Part B):")}
                        </span>{" "}
                        Select whether you operate as a <span className="font-medium">{translateUi("Railway Undertaking")}</span> (TOC or FOC) or{" "}
                        <span className="font-medium">{translateUi("Infrastructure Manager")}</span> {translateUi(". This choice is applied to Train Driver Licencing on the Complementary Certificate (Part B).")} </p>
                      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 min-w-0">
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                            <Lock className="w-3.5 h-3.5 text-slate-400" />
                            {translateUi("Company name")}
                          </label>
                          <p className="text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-xl px-4 py-2">
                            {company.name || company.companyCode || company.id || companyDetails.name || "—"}
                          </p>
                          <p className="text-xs text-slate-500">{translateUi("Set by your system administrator when the company was created.")}</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider"> {translateUi("VAT number (optional)")} </label>
                          <input
                            type="text"
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            value={companyDetails.vatNumber}
                            onChange={(e) => setCompanyDetails((prev) => ({ ...prev, vatNumber: e.target.value }))}
                            placeholder="e.g. GB123456789 — leave blank if not applicable"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Full postal address")}<RequiredMark /></label>
                          <textarea
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm min-h-[88px]"
                            value={companyDetails.postalAddress}
                            onChange={(e) => setCompanyDetails((prev) => ({ ...prev, postalAddress: e.target.value, address: e.target.value }))}
                            placeholder="Building, street, city, postcode, country"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider"> {translateUi("Registered company address (if different)")} </label>
                          <textarea
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm min-h-[72px]"
                            value={companyDetails.registeredAddressIfDifferent}
                            onChange={(e) => setCompanyDetails((prev) => ({ ...prev, registeredAddressIfDifferent: e.target.value }))}
                            placeholder="Leave blank if the same as postal address above"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Company contact number")}<RequiredMark /></label>
                          <input
                            type="tel"
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            value={companyDetails.companyPhone}
                            onChange={(e) => setCompanyDetails((prev) => ({ ...prev, companyPhone: e.target.value }))}
                            placeholder="Switchboard or main line"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                            <Lock className="w-3.5 h-3.5 text-slate-400" />
                            {translateUi("Company contact email")}
                          </label>
                          <p className="text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-xl px-4 py-2">
                            {lockedInviteCompanyEmail || "—"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {translateUi("Locked to the email address the company invite was sent to.")}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="rv-company-contact-name" className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                            {translateUi("Company contact full name")}<RequiredMark />
                          </label>
                          <input
                            id="rv-company-contact-name"
                            type="text"
                            autoComplete="name"
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            value={companyDetails.contactName}
                            onChange={(e) => setCompanyDetails((prev) => ({ ...prev, contactName: e.target.value }))}
                            placeholder="Full name of the primary company contact"
                          />
                          <p className="text-xs text-slate-500">
                            {translateUi("Used for company records and when assigning this contact as company administrator.")}
                          </p>
                        </div>
                        <div className="space-y-3 md:col-span-2">
                          <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider">{translateUi("Operator type")}<RequiredMark /></span>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 bg-white p-4 flex-1 has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50/50">
                              <input
                                type="radio"
                                name="operator-type"
                                className="mt-1"
                                checked={companyDetails.operatorType === "railway_undertaking"}
                                onChange={() => setCompanyDetails((prev) => ({ ...prev, operatorType: "railway_undertaking" }))}
                              />
                              <span>
                                <span className="block text-sm font-semibold text-slate-900">{translateUi("Railway Undertaking")}</span>
                                <span className="block text-xs text-slate-600 mt-0.5">{translateUi("Any public or private company whose primary business is to provide rail transport services for passengers or goods.")}</span>
                              </span>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 bg-white p-4 flex-1 has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50/50">
                              <input
                                type="radio"
                                name="operator-type"
                                className="mt-1"
                                checked={companyDetails.operatorType === "infrastructure_manager"}
                                onChange={() => setCompanyDetails((prev) => ({ ...prev, operatorType: "infrastructure_manager" }))}
                              />
                              <span>
                                <span className="block text-sm font-semibold text-slate-900">{translateUi("Infrastructure Manager")}</span>
                                <span className="block text-xs text-slate-600 mt-0.5">{translateUi("The entity responsible for establishing, managing, and maintaining infrastructure assets of a railway network.")}</span>
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5 space-y-3">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider"> {translateUi("Company logo (required)")} </p>
                        <p className="text-xs text-slate-600"> {translateUi("Upload your company logo before continuing. Required for the complementary certificate (Part B) and company branding. Recommended: square or landscape, max 2MB.")} </p>
                        <div className="flex items-center gap-4 flex-wrap">
                          {logoUrl && (
                            <div className="flex items-start gap-3">
                              <div className="w-20 h-20 border border-slate-200 rounded-xl overflow-hidden bg-white flex-shrink-0">
                                <img src={logoUrl} alt={translateUi("Company logo")} className="w-full h-full object-contain" />
                              </div>
                              <button
                                type="button"
                                disabled={uploadingLogo || deletingLogo}
                                onClick={async () => {
                                  setLogoUploadError(null);
                                  if (previewMode) {
                                    setLogoUrl(null);
                                    return;
                                  }
                                  setDeletingLogo(true);
                                  try {
                                    const headers: Record<string, string> = { "Content-Type": "application/json" };
                                    if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
                                    const companyCodesToTry = Array.from(
                                      new Set(
                                        [companyCode, company.companyCode, company.id]
                                          .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
                                          .map((v) => v.trim())
                                      )
                                    );
                                    let removed = false;
                                    let apiError: string | null = null;
                                    for (const codeToTry of companyCodesToTry) {
                                      const apiRes = await fetch(`/api/companies/${encodeURIComponent(codeToTry)}/logo`, {
                                        method: "PATCH",
                                        headers,
                                        body: JSON.stringify({ logoUrl: null }),
                                      });
                                      const apiData = await apiRes.json().catch(() => ({} as { success?: boolean; error?: string }));
                                      if (apiRes.ok && apiData.success !== false) {
                                        removed = true;
                                        break;
                                      }
                                      apiError = apiData.error || apiError || "Failed to remove logo";
                                    }
                                    if (removed) {
                                      setLogoUrl(null);
                                    } else {
                                      setLogoUploadError(apiError || "Failed to remove logo");
                                    }
                                  } catch (err) {
                                    const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Failed to remove logo";
                                    setLogoUploadError(msg);
                                  } finally {
                                    setDeletingLogo(false);
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                                title={translateUi("Remove logo")}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                {deletingLogo ? "Removing..." : translateUi("Delete")}
                              </button>
                            </div>
                          )}
                          <label className="inline-flex items-center px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 cursor-pointer">
                            <Upload className="w-4 h-4 mr-2" />
                            {logoUrl ? "Change logo" : translateUi("Upload logo")}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={uploadingLogo || deletingLogo}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setLogoUploadError(null);
                                setLogoUploadProgress(0);
                                setLogoUploadPhase("preparing");
                                setUploadingLogo(true);
                                let savingPulse: ReturnType<typeof window.setInterval> | null = null;
                                const startSavingPulse = (from = 92) => {
                                  setLogoUploadPhase("saving");
                                  setLogoUploadProgress((p) => Math.max(p, from));
                                  if (savingPulse != null) return;
                                  savingPulse = window.setInterval(() => {
                                    setLogoUploadProgress((p) => {
                                      if (p >= 99) return p;
                                      return Math.min(99, p + 1);
                                    });
                                  }, 200);
                                };
                                const stopSavingPulse = () => {
                                  if (savingPulse != null) {
                                    window.clearInterval(savingPulse);
                                    savingPulse = null;
                                  }
                                };
                                try {
                                  setLogoUploadProgress(8);
                                  const dataUrl = await fileToLogoDataUrl(file);
                                  if (previewMode) {
                                    setLogoUploadProgress(100);
                                    setLogoUrl(dataUrl);
                                    await new Promise((r) => window.setTimeout(r, 200));
                                    return;
                                  }
                                  setLogoUploadProgress(20);
                                  setLogoUploadPhase("uploading");
                                  const headers: Record<string, string> = { "Content-Type": "application/json" };
                                  if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;

                                  // Prefer the active onboarding company code only — retrying alternate codes
                                  // multiplies hang time when Storage is slow.
                                  const codeToTry = String(companyCode || company.companyCode || company.id || "").trim();
                                  if (!codeToTry) throw new Error("Company code missing — cannot upload logo.");

                                  const { ok, data: apiData } = await patchCompanyLogoWithProgress({
                                    companyCode: codeToTry,
                                    dataUrl,
                                    headers,
                                    timeoutMs: 45000,
                                    onUploadProgress: (pct) => {
                                      // Network upload: 20–90%
                                      setLogoUploadProgress(20 + Math.round(pct * 0.7));
                                    },
                                    onProcessing: () => startSavingPulse(92),
                                  });
                                  if (ok && apiData.success !== false && apiData.logoUrl) {
                                    stopSavingPulse();
                                    setLogoUploadProgress(100);
                                    setLogoUrl(apiData.logoUrl);
                                    await new Promise((r) => window.setTimeout(r, 500));
                                  } else if (azureOnly) {
                                    if (ok && apiData.success !== false) {
                                      stopSavingPulse();
                                      setLogoUploadProgress(100);
                                      setLogoUrl(apiData.logoUrl || dataUrl);
                                      await new Promise((r) => window.setTimeout(r, 500));
                                    } else {
                                      setLogoUploadError(apiData.error || "Failed to upload logo");
                                    }
                                  } else {
                                    setLogoUploadError(apiData.error || "Failed to upload logo");
                                  }
                                } catch (err) {
                                  const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Failed to upload logo";
                                  setLogoUploadError(msg);
                                } finally {
                                  stopSavingPulse();
                                  setUploadingLogo(false);
                                  setLogoUploadProgress(0);
                                  setLogoUploadPhase("preparing");
                                  e.target.value = "";
                                }
                              }}
                            />
                          </label>
                        </div>
                        {uploadingLogo && (
                          <div
                            className="rounded-xl border border-slate-200 bg-slate-900/90 px-3 py-3 space-y-3 shadow-sm"
                            role="status"
                            aria-live="polite"
                            aria-busy="true"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs font-semibold text-white">
                                {logoUploadPhase === "preparing"
                                  ? "Preparing logo…"
                                  : logoUploadPhase === "saving"
                                    ? translateUi("Saving logo…")
                                    : translateUi("Uploading logo…")}
                              </p>
                              <p className="text-sm font-bold tabular-nums text-white">{logoUploadProgress}%</p>
                            </div>
                            <div className="relative flex h-16 w-full items-center overflow-hidden rounded-lg bg-slate-950/40">
                              <div className="absolute left-0 right-0 top-[60%] h-1 -translate-y-1/2 rounded-full bg-white/40" />
                              <div
                                className="pointer-events-none absolute top-[60%] z-[2] w-max"
                                style={{ animation: "rv-logo-upload-train-scroll 4s linear infinite" }}
                              >
                                <div className="flex items-center">
                                  <img
                                    src="/TractionHeaderImage.png"
                                    alt=""
                                    className="block h-12 w-auto max-w-none select-none object-contain object-left drop-shadow-md"
                                  />
                                  <span
                                    className="block drop-shadow-md"
                                    style={{ transform: "scaleX(-1)" }}
                                    aria-hidden="true"
                                  >
                                    {/* Crop to leading power car only (sprite cols 0–301 of 600). */}
                                    <span
                                      className="block h-12 overflow-hidden"
                                      style={{ width: "calc(3rem * 302 / 75)" }}
                                    >
                                      <img
                                        src="/TractionHeaderImage.png"
                                        alt=""
                                        className="block h-12 w-auto max-w-none select-none object-contain object-left"
                                      />
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/20">
                              <div
                                className="h-full rounded-full bg-sky-400 transition-[width] duration-150 ease-out"
                                style={{ width: `${Math.max(2, Math.min(100, logoUploadProgress))}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {logoUploadError && <p className="text-xs text-rose-600">{logoUploadError}</p>}
                      </div>

                      {companyDetailsError && (
                        <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{companyDetailsError}</div>
                      )}
                      <div className="mt-6 space-y-2">
                        <button
                          type="button"
                          disabled={
                            companyDetailsSaving ||
                            !companyDetails.postalAddress.trim() ||
                            !companyDetails.companyPhone.trim() ||
                            !companyDetails.contactName.trim() ||
                            !lockedInviteCompanyEmail ||
                            !companyDetails.operatorType ||
                            !logoUrl ||
                            uploadingLogo
                          }
                          onClick={async () => {
                            setCompanyDetailsError(null);
                            if (previewMode) {
                              if (!editorMode) setActiveStep("job-roles");
                              return;
                            }
                            if (!currentUserEmail) {
                              setCompanyDetailsError("Your session must include an email to save company details.");
                              return;
                            }
                            if (!lockedInviteCompanyEmail) {
                              setCompanyDetailsError("Company contact email from the invite is missing. Contact your system administrator.");
                              return;
                            }
                            if (companyDetails.contactName.trim().length < 2) {
                              setCompanyDetailsError("Enter the company contact full name.");
                              return;
                            }
                            if (!logoUrl) {
                              setCompanyDetailsError("Upload your company logo to continue.");
                              return;
                            }
                            setCompanyDetailsSaving(true);
                            try {
                              const headers: Record<string, string> = {
                                "Content-Type": "application/json",
                                "X-User-Email": currentUserEmail,
                                "X-Company-Code": companyCode,
                              };
                              const res = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/company-details`, {
                                method: "PATCH",
                                headers,
                                body: JSON.stringify({
                                  companyCode,
                                  postalAddress: companyDetails.postalAddress.trim(),
                                  vatNumber: companyDetails.vatNumber.trim(),
                                  registeredAddressIfDifferent: companyDetails.registeredAddressIfDifferent.trim(),
                                  companyPhone: companyDetails.companyPhone.trim(),
                                  companyEmail: lockedInviteCompanyEmail,
                                  contactName: companyDetails.contactName.trim(),
                                  operatorType: companyDetails.operatorType,
                                }),
                              });
                              const data = await res.json().catch(() => ({} as { error?: string }));
                              if (!res.ok) throw new Error(data.error || "Failed to save company details");
                              markStepSaved("company-details");
                              if (!editorMode) setActiveStep("job-roles");
                            } catch (err) {
                              const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "Failed to save";
                              setCompanyDetailsError(msg);
                            } finally {
                              setCompanyDetailsSaving(false);
                            }
                          }}
                          className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {companyDetailsSaving ? "Saving…" : editorMode ? translateUi("Save changes") : "Save & continue to job roles →"}
                        </button>
                        <RequiredFieldsHelper
                          missing={[
                            ...(!companyDetails.postalAddress.trim() ? ["Enter the full postal address"] : []),
                            ...(!companyDetails.companyPhone.trim() ? ["Enter the company contact number"] : []),
                            ...(!companyDetails.contactName.trim() ? ["Enter the company contact full name"] : []),
                            ...(!lockedInviteCompanyEmail ? ["Company contact email from invite is missing"] : []),
                            ...(!companyDetails.operatorType ? ["Select an operator type"] : []),
                            ...(!logoUrl ? ["Upload your company logo"] : []),
                            ...(uploadingLogo ? ["Wait for the logo upload to finish"] : []),
                          ]}
                        />
                        {editorMode && savedNoticeByStep["company-details"] && (
                          <span className="text-xs font-bold text-emerald-700">{savedNoticeByStep["company-details"]}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}


              {activeStep === "framework" && (
                <div
                  key="framework"
                  className="rv-setup-step space-y-4"
                >
                  <h3 className="text-lg font-bold text-slate-900"> {translateUi("Framework (competency cycle)")} </h3>
                  <p className="text-sm text-slate-500">
                    {translateUi("Select a standard cycle to use as your company&apos;s competency framework. This will be available in the Cycles tab when assigning competency cycles to employees. To help you get started quickly, we&apos;ve included a couple of commonly used cycles, but you can also create your own custom standard cycles for each role using the Cycle Builder. You can add multiple cycles.")}
                  </p>
                  {appliedFrameworks.length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{translateUi("Applied cycles")}</p>
                      <ul className="space-y-1">
                        {appliedFrameworks.map((f) => (
                          <li key={f.id} className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                            {f.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <label htmlFor="framework-cycle" className="text-sm font-medium text-slate-700">
                      {translateUi("Add cycle")}
                    </label>
                    <select
                      id="framework-cycle"
                      value={frameworkCycleId}
                      onChange={(e) => {
                        setFrameworkCycleId(e.target.value);
                        setFrameworkError(null);
                      }}
                      className="w-full sm:w-auto px-4 py-2 border border-slate-200 rounded-xl bg-white text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 outline-none min-w-0 sm:min-w-[220px] max-w-full"
                    >
                      <option value="">{translateUi("— Choose a cycle —")}</option>
                      {availableCycleOptions.filter((c) => !appliedFrameworks.some((f) => f.cycleId === c.id)).map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={!frameworkCycleId || applyingCycle}
                      onClick={async () => {
                        if (!frameworkCycleId) return;
                        const cycle = availableCycleOptions.find((c) => c.id === frameworkCycleId);
                        if (!cycle) return;
                        setApplyingCycle(true);
                        setFrameworkError(null);
                        try {
                          const headers: Record<string, string> = { "Content-Type": "application/json" };
                          if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
                          let success = false;
                          let errorMessage: string | null = null;

                          const apiRes = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/apply-framework?v=${Date.now()}`, {
                            method: "POST",
                            headers,
                            body: JSON.stringify({ cycleId: cycle.id, cycleName: cycle.name }),
                          });
                          if (apiRes.ok) {
                            const data = await apiRes.json().catch(() => ({} as { success?: boolean; error?: string }));
                            success = data.success !== false;
                            if (!success) errorMessage = data.error || null;
                          }

                          if (!success) {
                            if (azureOnly) {
                              // Azure API only — do not attempt SQL fallback.
                              errorMessage = errorMessage || "Failed to apply framework";
                            } else {
                              const res = await applyStandardCycle({
                                companyId: companyCode,
                                cycleId: cycle.id,
                                cycleName: cycle.name,
                                userId: currentUserId,
                              });
                              success = res.success;
                              if (!success) errorMessage = errorMessage || res.error || null;
                            }
                          }
                          setApplyingCycle(false);
                          if (success) {
                            setFrameworkCycleId("");
                            await fetchAppliedFrameworks();
                            const issues = await fetchValidation();
                            setValidationIssues(issues);
                          } else {
                            setFrameworkError(errorMessage ?? "Failed to apply");
                          }
                        } catch {
                          setApplyingCycle(false);
                          setFrameworkError("Failed to apply");
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!frameworkCycleId ? translateUi("Choose a cycle from the list first") : undefined}
                    >
                      {applyingCycle ? translateUi("Applying…") : translateUi("Add cycle")}
                    </button>
                  </div>
                  {!frameworkCycleId && (
                    <RequiredFieldsHelper missing={["Choose a competency cycle from the list above"]} />
                  )}
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{translateUi("Need a custom cycle?")}</p>
                        <p className="text-xs text-slate-600">{translateUi("Create one here and choose units/elements from templates, then add it above.")}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowCycleBuilder((prev) => !prev)}
                        className="px-3 py-2 rounded-lg border border-indigo-200 bg-white text-indigo-700 text-sm font-medium hover:bg-indigo-50"
                      >
                        {showCycleBuilder ? translateUi("Hide builder") : translateUi("Create custom cycle")}
                      </button>
                    </div>
                    {showCycleBuilder && (
                      <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <CycleWizardPage
                          currentUserEmail={currentUserEmail ?? null}
                          companyCode={companyCode}
                          isSystemAdmin={false}
                          showFrameworkPanes={false}
                          onSaved={async () => {
                            await fetchAvailableCycles();
                            setFrameworkError(null);
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {availableCyclesLoading && (
                    <p className="text-xs text-slate-500">{translateUi("Refreshing available cycles…")}</p>
                  )}
                  {frameworkError && (
                    <p className="text-sm text-red-600">{frameworkError}</p>
                  )}
                  {editorMode ? (
                    <p className="text-xs text-slate-500">
                      {translateUi("Framework changes save immediately. No additional onboarding steps are required for an already active company.")}
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-slate-500">
                        {translateUi("Add at least one cycle to continue. You can add more later or create custom cycles in the Cycle Builder.")}
                      </p>
                      <div className="flex flex-col items-end gap-2 pt-4 border-t border-slate-200">
                        <button
                          type="button"
                          disabled={appliedFrameworks.length === 0}
                          onClick={() => setActiveStep("company-admin")}
                          className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {translateUi("Save & continue")}
                        </button>
                        <RequiredFieldsHelper
                          missing={appliedFrameworks.length === 0 ? ["Add at least one competency cycle"] : []}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeStep === "addons" && (
                <div key="addons" className="rv-setup-step space-y-5 max-w-4xl min-w-0 w-full">
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <LayoutDashboard className="w-5 h-5 text-violet-600" />
                      {translateUi("Activate bolt-on modules")}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Optional add-ons extend Rail Intel into a fuller suite of operational tools. Activate or purchase
                      modules that fit your company now, or continue and do this later from Administration → Add-ons.
                      This list updates automatically as new modules become available.
                    </p>
                  </div>
                  <div
                    className={cn(
                      "relative overflow-hidden rounded-3xl border p-4 sm:p-5",
                      isDarkMode
                        ? "border-slate-600 bg-slate-900/90"
                        : "border-slate-200/80 bg-gradient-to-br from-slate-100/80 via-sky-50/50 to-violet-50/40"
                    )}
                  >
                    {!isDarkMode && (
                      <>
                    <div
                      className="pointer-events-none absolute top-0 right-0 h-40 w-40 rounded-full bg-sky-200/30 blur-3xl"
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 left-0 h-44 w-44 rounded-full bg-violet-200/25 blur-3xl"
                      aria-hidden
                    />
                      </>
                    )}
                  {addonsLoading && (
                    <p className="relative z-[1] text-sm text-slate-500">{translateUi("Loading add-on catalogue…")}</p>
                  )}
                  {addonsError && <p className="relative z-[1] text-sm text-amber-700">{addonsError}</p>}
                  {addonsMessage && <p className="relative z-[1] text-sm text-emerald-700 font-medium">{addonsMessage}</p>}
                  <div className="relative z-[1] grid grid-cols-1 md:grid-cols-2 gap-4">
                    {onboardingAddons.map((mod) => {
                      const accent =
                        mod.accent === "violet"
                          ? { icon: "bg-violet-500/15 text-violet-700 ring-1 ring-violet-200/50", btn: "bg-violet-600/90 hover:bg-violet-700 shadow-lg shadow-violet-200/40" }
                          : mod.accent === "emerald"
                            ? { icon: "bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-200/50", btn: "bg-emerald-600/90 hover:bg-emerald-700 shadow-lg shadow-emerald-200/40" }
                            : mod.accent === "cyan"
                              ? { icon: "bg-cyan-500/15 text-cyan-800 ring-1 ring-cyan-200/50", btn: "bg-cyan-600/90 hover:bg-cyan-700 shadow-lg shadow-cyan-200/40" }
                              : mod.accent === "amber"
                                ? { icon: "bg-amber-500/15 text-amber-900 ring-1 ring-amber-200/50", btn: "bg-amber-600/90 hover:bg-amber-700 shadow-lg shadow-amber-200/40" }
                              : { icon: "bg-indigo-500/15 text-indigo-700 ring-1 ring-indigo-200/50", btn: "bg-indigo-600/90 hover:bg-indigo-700 shadow-lg shadow-indigo-200/40" };
                      const Icon =
                        mod.id === "task-assignment" || mod.id === "driver-reports"
                          ? ClipboardList
                          : mod.id === "leave-absence"
                            ? Calendar
                          : mod.id === "trainee-driver"
                            ? GraduationCap
                            : mod.id === "safety-briefs"
                              ? Shield
                              : FileCheck;
                      return (
                        <div
                          key={mod.id}
                          className={cn(
                            "relative overflow-hidden rounded-2xl border-2 p-5 flex flex-col gap-3 transition-shadow",
                            isDarkMode
                              ? "border-slate-600 bg-slate-800/90 shadow-none ring-1 ring-slate-600/50"
                              : "border-white/65 bg-white/45 shadow-[0_20px_48px_rgba(15,23,42,0.08)] ring-1 ring-sky-100/35 backdrop-blur-md hover:shadow-[0_24px_56px_rgba(15,23,42,0.12)]"
                          )}
                        >
                          {!isDarkMode && (
                            <>
                          <div
                            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/50 via-white/10 to-sky-100/20"
                            aria-hidden
                          />
                          <div
                            className="pointer-events-none absolute inset-[2px] rounded-[inherit] border border-white/40"
                            aria-hidden
                          />
                          <div
                            className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/55 to-transparent"
                            aria-hidden
                          />
                            </>
                          )}
                          <div className="relative z-[1] flex items-start gap-3">
                            <div className={`shrink-0 p-2.5 rounded-xl backdrop-blur-sm ${accent.icon}`}>
                              <Icon className="w-5 h-5" aria-hidden />
                            </div>
                            <div className="min-w-0 space-y-1">
                              <p className="text-base font-bold text-slate-900">{mod.name}</p>
                              <p className="text-sm text-slate-600 leading-relaxed">{mod.shortDescription}</p>
                            </div>
                          </div>
                          <div
                            className={cn(
                              "relative z-[1] rounded-xl border px-3 py-2.5 backdrop-blur-sm",
                              isDarkMode
                                ? "border-slate-600 bg-slate-900/70 ring-1 ring-slate-600/40"
                                : "border-white/50 bg-white/35 ring-1 ring-sky-100/25"
                            )}
                          >
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                              {translateUi("How it helps")}
                            </p>
                            <p className="text-xs text-slate-700 leading-relaxed">{mod.howItHelps}</p>
                          </div>
                          {mod.activated ? (
                            <button
                              type="button"
                              disabled
                              className="relative z-[1] mt-auto inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-emerald-200/80 bg-emerald-50/80 text-emerald-900 cursor-default backdrop-blur-sm"
                            >
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-hidden />
                              {translateUi("Activated")}
                            </button> ) : mod.isFree ? ( <button
                              type="button"
                              disabled={addonActionId === mod.id || !currentUserEmail}
                              onClick={() => void handleActivateOrPurchaseAddon(mod, "free")}
                              className={`relative z-[1] mt-auto px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm ${accent.btn}`}
                            >
                              {addonActionId === mod.id ? translateUi("Activating…") : translateUi("Activate (free)")}
                            </button>
                          ) : (
                            <div className="relative z-[1] mt-auto space-y-2">
                              <p className="text-[11px] text-slate-600 leading-relaxed">
                                {(mod.trialDays || 14)}-day trial available
                                {mod.stripeReady
                                  ? translateUi(" — no charge until the trial ends after you subscribe.")
                                  : translateUi(" — activate now and subscribe later from Administration → Add-ons.")}
                              </p>
                              {mod.trialAvailable !== false ? (
                                <button
                                  type="button"
                                  disabled={addonActionId === mod.id || !currentUserEmail}
                                  onClick={() => void handleActivateOrPurchaseAddon(mod, "trial")}
                                  className={`w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm ${accent.btn}`}
                                >
                                  {addonActionId === mod.id
                                    ? mod.stripeReady
                                      ? translateUi("Opening checkout…")
                                      : translateUi("Starting trial…")
                                    : `Start ${mod.trialDays || 14}-day trial`}
                                </button>
                              ) : null}
                              {mod.stripeReady ? (
                                <button
                                  type="button"
                                  disabled={addonActionId === mod.id || !currentUserEmail}
                                  onClick={() => void handleActivateOrPurchaseAddon(mod, "purchase")}
                                  className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold border border-slate-300/80 bg-white/70 text-slate-800 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
                                >
                                  {addonActionId === mod.id ? "Redirecting to checkout…" : "Purchase / subscribe"}
                                </button>
                              ) : mod.trialAvailable === false ? (
                                <p className="text-xs text-amber-700 font-medium">
                                  {translateUi("Trial already used. Ask your system administrator to complete Stripe settings so you can subscribe.")}
                                </p>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => setActiveStep("job-roles")}
                      className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      {translateUi("Back to job roles")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        markStepSaved("addons");
                        if (!editorMode) setActiveStep("company-admin");
                      }}
                      className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700"
                    >
                      {editorMode
                        ? translateUi("Done")
                        : onboardingAddons.some((m) => m.activated)
                          ? translateUi("Continue →")
                          : translateUi("Continue without activating →")}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    {onboardingAddons.some((m) => m.activated)
                      ? "You can activate or purchase more modules later from Administration → Add-ons."
                      : "You can activate or purchase any module later. Core onboarding does not require add-ons."}
                  </p>
                </div>
              )}

              {activeStep === "company-admin" && (
                <div key="company-admin" className="rv-setup-step space-y-5">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-indigo-600" />
                    {translateUi("Nominate company administrator")}
                  </h3>
                  <div className="space-y-3 text-sm text-slate-600">
                    <p>
                      Choose who will take responsibility for completing your organisation&apos;s configuration in Rail Intel after this onboarding step.
                      They should be senior enough to own competency frameworks, grading, role mappings, company standards, and team access—not only day-to-day data entry.
                    </p>
                    <p className="text-slate-700">
                      {translateUi("No email is sent at this step. If you nominate a")} <strong>{translateUi("different person")}</strong>{translateUi(", activation creates their administrator login and emails a temporary password. If you use the")} <strong>{translateUi("existing company contact")}</strong> {translateUi(", they already have login details from the company invite — activation only grants administrator access (no new login or welcome email).")} </p>
                    <p className="text-slate-700">
                      {translateUi("After sign-in they should complete any remaining work under")} <strong> {translateUi("Administration")} </strong>{translateUi("—frameworks, role mappings, grading, standards—before the platform is relied on for operational assessments or compliance records.")}
                    </p>
                    <p className="text-xs text-slate-500"> {translateUi("After activation, the nominated administrator chooses the company grading scale under Administration → Grading scale (nothing is pre-selected).")} </p>
                  </div>
                  <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 space-y-1.5" role="note">
                    <p className="text-sm font-semibold text-sky-950">{translateUi("Company administrator seats")}</p>
                    <p className="text-sm text-sky-900">
                      {translateUi("Your company includes")} <strong> {translateUi("2 company administrator seats")} </strong> {translateUi(". Additional seats can be purchased later from Administration → Add-ons (Company Admin Licences).")} </p>
                  </div>
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-2">
                    <p className="text-sm font-semibold text-amber-950">{translateUi("Important: authority you are assigning")}</p>
                    <ul className="text-sm text-amber-900 list-disc ml-5 space-y-1">
                      <li>{translateUi("Company administrators typically have full visibility of employee competency, medical summaries (where permitted), incidents, reporting, cycle configuration, and team management settings for your company code.")}</li>
                      <li>{translateUi("They can delegate work to others, but the role is powerful, only nominate someone trusted and authorised by your organisation.")}</li>
                      <li>{translateUi("Nominating an incorrect person may expose sensitive workforce and safety data.")}</li>
                    </ul>
                  </div>

                  <fieldset className="space-y-3">
                    <legend className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
                      {translateUi("Who should be the company administrator?")}
                    </legend>
                    <label
                      className={cn(
                        "flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors",
                        adminNominationChoice === "use-contact"
                          ? "border-indigo-300 bg-indigo-50/60"
                          : "border-slate-200 bg-white hover:border-slate-300",
                        !existingContactForAdmin.available && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      <input
                        type="radio"
                        name="rv-admin-nomination-choice"
                        className="mt-1 w-4 h-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={adminNominationChoice === "use-contact"}
                        disabled={!existingContactForAdmin.available}
                        onChange={() => {
                          setAdminNominationChoice("use-contact");
                          setNominationError(null);
                          applyExistingContactAsAdmin();
                        }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-900">{translateUi("Use existing company contact")}</span>
                        <span className="block text-xs text-slate-600 mt-0.5">
                          {existingContactForAdmin.available ? (
                            <>
                              Assign company administrator access to the invite contact
                              {existingContactForAdmin.name ? (
                                <>
                                  {" "}
                                  (<strong>{existingContactForAdmin.name}</strong>)
                                </>
                              ) : null}{" "}
                              ({existingContactForAdmin.email}). They already have login details — no new welcome email on
                              activation. This uses one of your included seats.
                            </>
                          ) : (
                            <>
                              The company invite email is required before this option is available. Complete Company
                              details first if it is missing.
                            </>
                          )}
                        </span>
                      </span>
                    </label>
                    <label
                      className={cn(
                        "flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors",
                        adminNominationChoice === "nominate-other"
                          ? "border-indigo-300 bg-indigo-50/60"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      )}
                    >
                      <input
                        type="radio"
                        name="rv-admin-nomination-choice"
                        className="mt-1 w-4 h-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={adminNominationChoice === "nominate-other"}
                        onChange={() => {
                          setAdminNominationChoice("nominate-other");
                          setNominationError(null);
                        }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-900">{translateUi("Nominate a different person")}</span>
                        <span className="block text-xs text-slate-600 mt-0.5">
                          {translateUi("Enter another colleague's work email, full name, and job title. They will receive the administrator invite when the company is activated.")}
                        </span>
                      </span>
                    </label>
                    <label
                      className={cn(
                        "flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors",
                        adminNominationChoice === "add-later"
                          ? "border-indigo-300 bg-indigo-50/60"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      )}
                    >
                      <input
                        type="radio"
                        name="rv-admin-nomination-choice"
                        className="mt-1 w-4 h-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={adminNominationChoice === "add-later"}
                        onChange={() => {
                          setAdminNominationChoice("add-later");
                          setNominationError(null);
                          setNominatedAdminEmail("");
                          setNominatedAdminName("");
                          setNominatedAdminJobTitle("");
                        }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-900">{translateUi("Add company administrator later")}</span>
                        <span className="block text-xs text-slate-600 mt-0.5">
                          {translateUi("Skip nomination for now and continue onboarding. You can nominate a company administrator later from Administration after activation.")}
                        </span>
                      </span>
                    </label>
                  </fieldset>

                  {adminNominationChoice === "use-contact" && existingContactForAdmin.available && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 space-y-3">
                      <p className="text-sm font-semibold text-slate-900">{translateUi("Contact details to use")}</p>
                      <div className="space-y-1">
                        <label htmlFor="rv-nominee-email-contact" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {translateUi("Work email")}
                        </label>
                        <p
                          id="rv-nominee-email-contact"
                          className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-700 bg-slate-100 break-all"
                        >
                          {existingContactForAdmin.email}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="rv-nominee-name-contact" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {translateUi("Full name")}<RequiredMark />
                        </label>
                        <input
                          id="rv-nominee-name-contact"
                          type="text"
                          autoComplete="name"
                          value={nominatedAdminName}
                          onChange={(e) => setNominatedAdminName(e.target.value)}
                          placeholder="Full name for the administrator welcome email"
                          className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                        />
                        {!existingContactForAdmin.name && (
                          <p className="text-xs text-slate-500">
                            {translateUi("No primary contact name was stored on the company record — enter the contact&apos;s full name here.")}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="rv-nominee-title-contact" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {translateUi("Job title for their administrator record")}<RequiredMark />
                        </label>
                        <input
                          id="rv-nominee-title-contact"
                          type="text"
                          value={nominatedAdminJobTitle}
                          onChange={(e) => setNominatedAdminJobTitle(e.target.value)}
                          placeholder="e.g. Company Administrator"
                          className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                        />
                      </div>
                    </div>
                  )}

                  {adminNominationChoice === "nominate-other" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2 space-y-1">
                        <label htmlFor="rv-nominee-email" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {translateUi("Work email address")}<RequiredMark />
                        </label>
                        <input
                          id="rv-nominee-email"
                          type="email"
                          autoComplete="email"
                          value={nominatedAdminEmail}
                          onChange={(e) => setNominatedAdminEmail(e.target.value)}
                          placeholder="alex.admin@company.com"
                          className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="rv-nominee-name" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {translateUi("Full name")}<RequiredMark />
                        </label>
                        <input
                          id="rv-nominee-name"
                          type="text"
                          autoComplete="name"
                          value={nominatedAdminName}
                          onChange={(e) => setNominatedAdminName(e.target.value)}
                          placeholder="As it should appear on their welcome email"
                          className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="rv-nominee-title" className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {translateUi("Job title")}<RequiredMark />
                        </label>
                        <input
                          id="rv-nominee-title"
                          type="text"
                          value={nominatedAdminJobTitle}
                          onChange={(e) => setNominatedAdminJobTitle(e.target.value)}
                          placeholder="e.g. Director of Operations"
                          className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                        />
                      </div>
                    </div>
                  )}

                  {nominationError && <p className="text-sm text-red-600">{nominationError}</p>}
                  <div className="flex flex-col gap-2 pt-4 border-t border-slate-200">
                    <div className="flex justify-between flex-wrap gap-3 items-center">
                    {!editorMode && hasNominationRecord && !nominateAdminLater && (
                      <span className="text-sm font-medium text-emerald-700">{translateUi("Nomination submitted. You can continue to review.")}</span>
                    )}
                    {!editorMode && nominationDeferred && nominateAdminLater && (
                      <span className="text-sm font-medium text-emerald-700">{translateUi("Saved to add later. You can continue to review.")}</span>
                    )}
                    <button
                      type="button"
                      disabled={
                        nominationSaving ||
                        (useExistingContactAsAdmin &&
                          (!existingContactForAdmin.available ||
                            nominatedAdminName.trim().length < 2 ||
                            nominatedAdminJobTitle.trim().length < 2)) ||
                        (adminNominationChoice === "nominate-other" &&
                          (!nominatedAdminEmail.trim() ||
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nominatedAdminEmail.trim()) ||
                            nominatedAdminName.trim().length < 2 ||
                            nominatedAdminJobTitle.trim().length < 2))
                      }
                      onClick={async () => {
                        setNominationError(null);
                        if (previewMode) {
                          if (nominateAdminLater) {
                            setNominationDeferred(true);
                            setHasNominationRecord(false);
                            setSavedNoticeByStep((prev) => ({
                              ...prev,
                              "company-admin": "You can nominate a company administrator later from Administration.",
                            }));
                          } else {
                            setHasNominationRecord(true);
                            setNominationDeferred(false);
                            setSavedNoticeByStep((prev) => ({
                              ...prev,
                              "company-admin": useExistingContactAsAdmin
                                ? "Company contact saved as company administrator."
                                : "Nomination saved.",
                            }));
                          }
                          if (!editorMode) setActiveStep("review");
                          return;
                        }
                        setNominationSaving(true);
                        try {
                          if (useExistingContactAsAdmin) {
                            setNominatedAdminEmail(existingContactForAdmin.email);
                            if (nominatedAdminName.trim().length < 2 && existingContactForAdmin.name.trim().length >= 2) {
                              setNominatedAdminName(existingContactForAdmin.name.trim());
                            }
                            if (nominatedAdminJobTitle.trim().length < 2) {
                              setNominatedAdminJobTitle(translateUi("Company Administrator"));
                            }
                          }
                          const headers: Record<string, string> = { "Content-Type": "application/json" };
                          if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
                          const companyCodesToTry = Array.from(
                            new Set(
                              [companyCode, company.companyCode, company.id]
                                .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
                                .map((v) => v.trim())
                            )
                          );
                          const payloadEmail = useExistingContactAsAdmin
                            ? existingContactForAdmin.email.trim()
                            : nominatedAdminEmail.trim();
                          const payloadName = useExistingContactAsAdmin
                            ? (nominatedAdminName.trim() || existingContactForAdmin.name.trim())
                            : nominatedAdminName.trim();
                          const payloadTitle = nominatedAdminJobTitle.trim() || "Company Administrator";
                          let ok = false;
                          let apiMessage: string | null = null;
                          let deferredResult = false;
                          for (const codeToTry of companyCodesToTry) {
                            const res = await fetch(`/api/companies/${encodeURIComponent(codeToTry)}/nominate-company-admin`, {
                              method: "POST",
                              headers,
                              body: JSON.stringify(
                                nominateAdminLater
                                  ? { defer: true }
                                  : {
                                      adminEmail: payloadEmail,
                                      adminFullName: payloadName,
                                      adminJobTitle: payloadTitle,
                                    }
                              ),
                            });
                            const data = await res.json().catch(() => ({} as { error?: string; success?: boolean; deferred?: boolean; message?: string }));
                            if (res.ok && data.success !== false) {
                              ok = true;
                              deferredResult = data.deferred === true;
                              if (typeof data.message === "string") apiMessage = data.message;
                              break;
                            }
                            apiMessage = typeof data.error === "string" ? data.error : "Failed to submit nomination";
                            if (res.status === 404) continue;
                          }
                          setNominationSaving(false);
                          if (ok) {
                            if (deferredResult || nominateAdminLater) {
                              setNominationDeferred(true);
                              setHasNominationRecord(false);
                              const notice =
                                (apiMessage && apiMessage.trim()) ||
                                "You can nominate a company administrator later from Administration.";
                              setSavedNoticeByStep((prev) => ({ ...prev, "company-admin": notice }));
                            } else {
                              setHasNominationRecord(true);
                              setNominationDeferred(false);
                              if (useExistingContactAsAdmin) {
                                setNominatedAdminEmail(payloadEmail);
                                setNominatedAdminName(payloadName);
                                setNominatedAdminJobTitle(payloadTitle);
                              }
                              const notice =
                                (apiMessage && apiMessage.trim()) ||
                                (useExistingContactAsAdmin
                                  ? "Company contact saved as company administrator. They already have login access from the invite — no new welcome email will be sent on activation."
                                  : "Nomination saved. The nominee will receive a welcome email when the company is activated.");
                              setSavedNoticeByStep((prev) => ({ ...prev, "company-admin": notice }));
                            }
                            setNominationError(null);
                            if (!editorMode) setActiveStep("review");
                          } else {
                            setNominationError(apiMessage || "Failed to submit nomination");
                          }
                        } catch {
                          setNominationSaving(false);
                          setNominationError("Failed to submit nomination");
                        }
                      }}
                      className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
                    >
                      {nominationSaving
                        ? "Saving…"
                        : nominateAdminLater
                          ? editorMode
                            ? translateUi("Save & continue later")
                            : translateUi("Continue without nominating")
                          : useExistingContactAsAdmin
                            ? editorMode
                              ? translateUi("Save contact as administrator")
                              : translateUi("Use contact & continue")
                            : editorMode
                              ? translateUi("Save nomination")
                              : translateUi("Submit & continue")}
                    </button>
                    </div>
                    <RequiredFieldsHelper
                      className="ml-auto"
                      missing={
                        nominateAdminLater
                          ? []
                          : useExistingContactAsAdmin
                            ? [
                                ...(!existingContactForAdmin.available
                                  ? ["Company invite email is required on Company details"]
                                  : []),
                                ...(nominatedAdminName.trim().length < 2
                                  ? ["Enter the contact's full name"]
                                  : []),
                                ...(nominatedAdminJobTitle.trim().length < 2
                                  ? ["Enter a job title for the administrator record"]
                                  : []),
                              ]
                            : [
                              ...(!nominatedAdminEmail.trim()
                                ? ["Enter a work email address"]
                                : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nominatedAdminEmail.trim())
                                  ? ["Enter a valid work email address"]
                                  : []),
                              ...(nominatedAdminName.trim().length < 2 ? ["Enter the administrator's full name"] : []),
                              ...(nominatedAdminJobTitle.trim().length < 2 ? ["Enter the administrator's job title"] : []),
                            ]
                      }
                    />
                  </div>
                  {savedNoticeByStep["company-admin"] && (
                    <p className="text-xs font-semibold text-emerald-700 max-w-prose">{savedNoticeByStep["company-admin"]}</p>
                  )}
                </div>
              )}

              {activeStep === "role-mapping" && (
                <div
                  key="role-mapping"
                  className="rv-setup-step space-y-5"
                >
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                    {translateUi("Role mapping")}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {translateUi("Map each job role to one or more competency cycles. When an employee is assigned a role, they will receive each linked cycle (e.g. 2-year period) with element criteria to complete. Add multiple cycles per role if needed. All criteria must be confirmed as competent, not applicable, advisory or review by the end of each cycle.")}
                  </p>
                  <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto overscroll-x-contain max-w-full">
                    <table className="w-full min-w-[36rem] text-sm">
                      <thead>
                        <tr className="bg-slate-100 border-b border-slate-200">
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">{translateUi("Job role")}</th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">{translateUi("Competency cycle")}</th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700 w-24"> {translateUi("Cycle length (years)")} </th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700 w-28"> {translateUi("Actions")} </th>
                        </tr>
                      </thead>
                      <tbody>
                        {roleMappingJobRoles.map((role) => {
                          const forRole = roleMappings.filter((m) => m.jobRoleName === role.name);
                          const rowsToShow =
                            forRole.length > 0
                              ? forRole
                              : [{ jobRoleName: role.name, cycleId: "", cycleName: "", cycleLengthYears: 2 }];
                          return rowsToShow.map((mapping, rowIdx) => {
                            const selectedElsewhere = (cid: string) =>
                              cid && forRole.some((m, i) => i !== rowIdx && m.cycleId === cid);
                            return (
                              <tr
                                key={`${role.name}-${rowIdx}-${mapping.cycleId || "new"}`}
                                className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                              >
                                {rowIdx === 0 ? (
                                  <td className="px-4 py-3 font-medium text-slate-800 align-top" rowSpan={rowsToShow.length}>
                                    {role.name}
                                  </td>
                                ) : null}
                                <td className="px-4 py-3">
                                  <select
                                    value={mapping.cycleId}
                                    onChange={(e) => {
                                      const cycleId = e.target.value;
                                      const cycle = appliedFrameworks.find((f) => f.cycleId === cycleId);
                                      if (cycleId && cycle) {
                                        setRoleMappings((prev) => {
                                          const forThis = prev.filter((m) => m.jobRoleName === role.name);
                                          if (forThis.length === 0) {
                                            return [
                                              ...prev,
                                              {
                                                jobRoleName: role.name,
                                                cycleId: cycle.cycleId,
                                                cycleName: cycle.name,
                                                cycleLengthYears: 2,
                                              },
                                            ];
                                          }
                                          let occ = 0;
                                          return prev.map((m) => {
                                            if (m.jobRoleName !== role.name) return m;
                                            if (occ === rowIdx) {
                                              occ++;
                                              return {
                                                jobRoleName: role.name,
                                                cycleId: cycle.cycleId,
                                                cycleName: cycle.name,
                                                cycleLengthYears: m.cycleLengthYears ?? 2,
                                              };
                                            }
                                            occ++;
                                            return m;
                                          });
                                        });
                                      } else {
                                        setRoleMappings((prev) => {
                                          const forThis = prev.filter((m) => m.jobRoleName === role.name);
                                          if (forThis.length === 0) return prev;
                                          let occ = 0;
                                          return prev.filter((m) => {
                                            if (m.jobRoleName !== role.name) return true;
                                            if (occ === rowIdx) {
                                              occ++;
                                              return false;
                                            }
                                            occ++;
                                            return true;
                                          });
                                        });
                                      }
                                    }}
                                    className="w-full sm:w-auto px-3 py-2 border border-slate-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none min-w-0 sm:min-w-[200px] max-w-full"
                                    disabled={appliedFrameworks.length === 0}
                                  >
                                    <option value="">{translateUi("— No cycle —")}</option>
                                    {appliedFrameworks.map((f) => (
                                      <option key={f.id} value={f.cycleId} disabled={selectedElsewhere(f.cycleId)}>
                                        {f.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="number"
                                    min={1}
                                    max={10}
                                    value={mapping.cycleLengthYears ?? 2}
                                    onChange={(e) => {
                                      const years = parseInt(e.target.value, 10);
                                      if (!isNaN(years) && years >= 1 && years <= 10) {
                                        setRoleMappings((prev) => {
                                          let occ = 0;
                                          return prev.map((m) => {
                                            if (m.jobRoleName !== role.name) return m;
                                            if (occ === rowIdx) {
                                              occ++;
                                              return { ...m, cycleLengthYears: years };
                                            }
                                            occ++;
                                            return m;
                                          });
                                        });
                                      }
                                    }}
                                    disabled={!mapping.cycleId}
                                    className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-slate-100 disabled:text-slate-400"
                                  />
                                </td>
                                <td className="px-4 py-3 align-middle">
                                  <div className="flex flex-wrap items-center gap-2">
                                    {rowIdx === rowsToShow.length - 1 ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setRoleMappings((prev) => [
                                            ...prev,
                                            { jobRoleName: role.name, cycleId: "", cycleName: "", cycleLengthYears: 2 },
                                          ])
                                        }
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                        disabled={appliedFrameworks.length === 0}
                                        title={translateUi("Add another cycle for this role")}
                                      >
                                        <Plus className="w-3.5 h-3.5" />
                                        {translateUi("Add cycle")}
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setRoleMappings((prev) => {
                                          let occ = 0;
                                          const next = prev.filter((m) => {
                                            if (m.jobRoleName !== role.name) return true;
                                            if (occ === rowIdx) {
                                              occ++;
                                              return false;
                                            }
                                            occ++;
                                            return true;
                                          });
                                          const still = next.filter((x) => x.jobRoleName === role.name);
                                          if (still.length === 0) {
                                            return [
                                              ...next,
                                              { jobRoleName: role.name, cycleId: "", cycleName: "", cycleLengthYears: 2 },
                                            ];
                                          }
                                          return next;
                                        });
                                      }}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-xs font-medium text-rose-700 hover:bg-rose-50"
                                      title={translateUi("Remove this row")}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" /> {translateUi("Remove")} </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })}
                      </tbody>
                    </table>
                    {roleMappingJobRoles.length === 0 && (
                      <p className="p-6 text-slate-500 text-sm">{translateUi("No job roles yet. Complete the Job roles step first.")}</p>
                    )}
                  </div>
                  {roleMappings.filter((m) => m.cycleId).length > 0 && (() => {
                    const mappedCycleIds = [...new Set(roleMappings.filter((m) => m.cycleId).map((m) => m.cycleId))];
                    const previewCycle = getCycleById(cycleStructurePreviewId || (mappedCycleIds[0] ?? ""));
                    return (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => setCycleStructureExpanded((e) => !e)}
                          className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-indigo-600"
                        >
                          {cycleStructureExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          View cycle structure (units, elements & criteria)
                        </button>
                        {cycleStructureExpanded && (
                          <div className="space-y-3">
                            {mappedCycleIds.length > 1 && (
                              <select
                                value={cycleStructurePreviewId || mappedCycleIds[0]}
                                onChange={(e) => setCycleStructurePreviewId(e.target.value)}
                                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none"
                              >
                                {mappedCycleIds.map((id) => {
                                  const m = roleMappings.find((r) => r.cycleId === id);
                                  return <option key={id} value={id}>{m?.cycleName ?? id}</option>;
                                })}
                              </select>
                            )}
                            {previewCycle ? (
                              <CycleStructureView cycle={previewCycle} defaultCollapsed={true} />
                            ) : (
                              <p className="text-sm text-slate-500 py-4">{translateUi("Cycle structure not available for this framework.")}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {roleMappingsError && (
                    <p className="text-sm text-red-600">{roleMappingsError}</p>
                  )}
                  <div className="flex flex-col gap-2 pt-4 border-t border-slate-200">
                    <div className="flex justify-between items-center gap-3 flex-wrap">
                    <span className="text-sm text-slate-500">
                      {roleMappings.filter((m) => m.cycleId).length} cycle mapping
                      {roleMappings.filter((m) => m.cycleId).length !== 1 ? "s" : ""} configured
                    </span>
                    <button
                      type="button"
                      onClick={async () => {
                        setRoleMappingsError(null);
                        setRoleMappingsSaving(true);
                        try {
                          const toSave = roleMappings.filter((m) => m.jobRoleName && m.cycleId);
                          if (azureOnly) {
                            const headers: Record<string, string> = {
                              "Content-Type": "application/json",
                              "X-Company-Code": companyCode,
                            };
                            if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
                            const apiRes = await fetch(`/api/companies/${encodeURIComponent(companyCode)}/role-mappings`, {
                              method: "POST",
                              headers,
                              body: JSON.stringify({ mappings: toSave }),
                            });
                            const apiData = await apiRes.json().catch(() => ({} as { error?: string; success?: boolean }));
                            if (apiRes.ok) {
                              markStepSaved("role-mapping");
                              if (!editorMode) setActiveStep("review");
                            } else {
                              setRoleMappingsError(apiData.error ?? translateUi("Failed to save"));
                            }
                          } else {
                            const res = await saveCompanyRoleMappings({
                              companyId: companyCode,
                              mappings: toSave,
                              userId: currentUserId,
                              userEmail: currentUserEmail,
                            });
                            if (res.success) {
                              markStepSaved("role-mapping");
                              if (!editorMode) setActiveStep("review");
                            } else {
                              setRoleMappingsError(res.error ?? translateUi("Failed to save"));
                            }
                          }
                          setRoleMappingsSaving(false);
                        } catch {
                          setRoleMappingsSaving(false);
                          setRoleMappingsError(translateUi("Failed to save"));
                        }
                      }}
                      disabled={roleMappingsSaving || roleMappings.filter((m) => m.cycleId).length === 0}
                      className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {roleMappingsSaving ? "Saving…" : editorMode ? translateUi("Save changes") : translateUi("Save & continue")}
                    </button>
                    </div>
                    <RequiredFieldsHelper
                      missing={
                        roleMappings.filter((m) => m.cycleId).length === 0
                          ? ["Map at least one job role to a competency cycle"]
                          : []
                      }
                    />
                    {editorMode && savedNoticeByStep["role-mapping"] && (
                      <span className="text-xs font-bold text-emerald-700">{savedNoticeByStep["role-mapping"]}</span>
                    )}
                  </div>
                </div>
              )}

              {activeStep === "review" && (
                <div
                  key="review"
                  className="rv-setup-step space-y-4"
                >
                  <h3 className="text-lg font-bold text-slate-900">
                    {translateUi("Review & confirm")}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {editorMode
                      ? "This company is already active. Use the step tabs to amend onboarding settings and save changes."
                      : "Complete onboarding now, then configure framework cycles and role mappings later from the admin Cycle Builder."}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border-2 border-white shadow-sm">
                        {!termsAcceptanceHydrated || termsAccepted ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-slate-900" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-rose-600" />
                        )}
                      </span>
                      <span>
                        Terms &amp; Conditions reviewed and accepted{" "}
                        {termsAcceptanceHydrated
                          ? termsAccepted
                            ? translateUi("(ok)")
                            : translateUi("(missing)")
                          : "(checking)"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border-2 border-white shadow-sm">
                        {validationIssues && validationIssues.missingCompanyLogo ? (
                          <XCircle className="w-3.5 h-3.5 text-rose-600" />
                        ) : (
                          <CheckCircle2 className="w-3.5 h-3.5 text-slate-900" />
                        )}
                      </span>
                      <span>
                        Company Details{" "}
                        {validationIssues &&
                          (validationIssues.missingCompanyLogo ? translateUi("(missing)") : translateUi("(ok)"))}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border-2 border-white shadow-sm">
                        {validationIssues && validationIssues.missingJobRoles ? (
                          <XCircle className="w-3.5 h-3.5 text-rose-600" />
                        ) : (
                          <CheckCircle2 className="w-3.5 h-3.5 text-slate-900" />
                        )}
                      </span>
                        <span>
                          Job roles defined{" "}
                          {validationIssues &&
                            (validationIssues.missingJobRoles
                              ? translateUi("(missing)")
                              : translateUi("(ok)"))}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border-2 border-white shadow-sm">
                          <CheckCircle2 className="w-3.5 h-3.5 text-slate-900" />
                        </span>
                        <span>
                          Add-ons{" "}
                          {(() => {
                            const activeCount = onboardingAddons.filter((m) => m.activated).length;
                            const total = onboardingAddons.length;
                            if (total === 0) return "(optional)";
                            if (activeCount === 0) return "(none activated — optional)";
                            return `(${activeCount} of ${total} activated)`;
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border-2 border-white shadow-sm">
                          {validationIssues && validationIssues.missingCompanyAdminNomination ? (
                            <XCircle className="w-3.5 h-3.5 text-rose-600" />
                          ) : (
                            <CheckCircle2 className="w-3.5 h-3.5 text-slate-900" />
                          )}
                        </span>
                      <span>
                        Company administrator{" "}
                        {validationIssues &&
                          (validationIssues.missingCompanyAdminNomination
                            ? "(missing)"
                            : nominationDeferred || nominateAdminLater
                              ? "(add later)"
                              : hasNominationRecord
                                ? translateUi("(nominated)")
                                : translateUi("(ok)"))}
                      </span>
                    </div>
                    {Boolean((validationIssues as unknown as { activateError?: string })?.activateError) && (
                      <div className="flex items-center space-x-2 text-rose-600">
                        <CircleX className="w-4 h-4" />
                        <span>{(validationIssues as unknown as { activateError: string }).activateError}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {translateUi("Terms acceptance, company details/logo, and job roles are required before activation. Add-ons are optional and can be activated later. A company administrator can be the existing company contact, a nominated person, or added later. Each company includes 2 administrator seats; further seats can be purchased from Add-ons.")}
                  </p>
                  {!editorMode && !previewMode && (
                    <>
                      {onboardingAddons.some((m) => m.activated) && (
                        <div
                          className="mt-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900"
                          role="note"
                        >
                          <span className="font-semibold">{translateUi("Activated add-ons:")} </span>
                          {onboardingAddons.filter((m) => m.activated).map((m) => m.name).join(", ")}
                        </div>
                      )}
                      <div
                        className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                        role="note"
                      >
                        <span className="font-semibold text-slate-800">{translateUi("Company administrator:")} </span>
                        {nominationDeferred || nominateAdminLater
                          ? "You chose to add a company administrator later. After activation, nominate them from Administration when ready."
                          : useExistingContactAsAdmin
                            ? "You chose to use the existing company contact as company administrator. They already have login details from the company invite — activation will grant administrator access without creating a new login or sending another welcome email."
                            : "Nominate and save on the Company administrator step (work email, full name, and job title), use the existing company contact, or choose Add later. If a different person is nominated now, their login and welcome email are created when you activate."}
                      </div>
                      <button
                        type="button"
                        disabled={
                          validating ||
                          !logoUrl ||
                          !termsAcceptanceHydrated ||
                          !termsAccepted ||
                          !!validationIssues?.missingJobRoles ||
                          !!validationIssues?.missingCompanyAdminNomination
                        }
                        onClick={handleActivate}
                        className="mt-4 inline-flex items-center px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-lg shadow-indigo-200 hover:bg-indigo-700 disabled:opacity-60"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {validating ? translateUi("Validating...") : translateUi("Activate company")}
                      </button>
                      <RequiredFieldsHelper
                        className="mt-2"
                        missing={[
                          ...(!termsAcceptanceHydrated || !termsAccepted
                            ? ["Accept Terms & Conditions (sidebar → Terms & Conditions)"]
                            : []),
                          ...(!logoUrl || validationIssues?.missingCompanyLogo
                            ? ["Upload company logo (sidebar → Company Details)"]
                            : []),
                          ...(validationIssues?.missingJobRoles
                            ? ["Add at least one job role (sidebar → Job roles)"]
                            : []),
                          ...(validationIssues?.missingCompanyAdminNomination
                            ? [
                                "Choose the company contact, nominate someone else, or add later (sidebar → Company administrator)",
                              ]
                            : []),
                        ]}
                      />
                    </>
                  )}
                </div>
              )}
            </>
            )}
            </div>
          </div>
        </div>
        </div>
      </div>
  );
};

